import { runtime } from '@/utils/runtime';
// ====== P2-O6 同步机制：全局 fetch monkey-patch，检测响应头 X-Merchant-Config-Version
// 当版本号上升时派发全局事件，由 auth.tsx 静默拉取最新配置（避免循环 import auth）
(function installMerchantConfigVersionInterceptor() {
  if (typeof window === 'undefined' || !(window as any).__merchantConfigInterceptorInstalled) {
    try { (window as any).__merchantConfigInterceptorInstalled = true; } catch (_) {}
    const _orig = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : null;
    if (!_orig) return;

    let _memVersion = -1;
    const getStoredVer = (): number => {
      try {
        const s = sessionStorage.getItem('auth_user');
        if (!s) return _memVersion;
        const u = JSON.parse(s);
        const v = Number(u?.merchantConfig?.configVersion);
        if (!isNaN(v)) { _memVersion = v; return v; }
      } catch (_) {}
      return _memVersion;
    };
    const checkResp = (resp: Response) => {
      try {
        const raw = resp.headers.get('X-Merchant-Config-Version');
        if (!raw) return;
        const server = Number(raw);
        if (isNaN(server) || server <= 0) return;
        const cur = getStoredVer();
        if (server > cur) {
          _memVersion = server;
          window.dispatchEvent(new CustomEvent('merchant-config-version-changed', { detail: { newVersion: server } }));
        }
      } catch (_) {}
    };
    const isRemoteApi = (input: RequestInfo | URL): boolean => {
      try {
        let url: string;
        if (typeof input === 'string') url = input;
        else if (input instanceof URL) url = input.toString();
        else url = (input as Request).url || '';
        if (!url) return false;
        // 只处理远程/本地 API 请求
        if (/\/api\//.test(url) || url.includes('localhost') || /^https?:\/\//.test(url)) return true;
      } catch (_) {}
      return false;
    };

    // 仅当本地存有 remote_token（确认是已登录态下的业务请求）遇到 401 时，触发统一踢下线
    // 避免登录接口 401、/register/*、/sms/*、/notices/* 等公开接口误触发
    const checkAuth401 = (input: RequestInfo | URL, resp: Response) => {
      if (resp.status !== 401) return;
      let url = '';
      try {
        if (typeof input === 'string') url = input;
        else if (input instanceof URL) url = input.toString();
        else url = (input as Request).url || '';
      } catch (_) {}
      if (!url) return;
      // 登录/注册/短信/公告等公开接口：401 属于正常校验失败，不触发 logout
      if (/\/api\/login($|\?)/.test(url) ||
          /\/api\/register/.test(url) ||
          /\/api\/(sms|notices)\//.test(url) ||
          /\/api\/remote\//.test(url) ||
          /\/api\/business\/lookup/.test(url)) return;
      let hasToken = false;
      try { hasToken = !!localStorage.getItem('remote_token'); } catch (_) { /* ignore */ }
      if (!hasToken) return;
      try {
        const bodyText = (resp as any).statusText || '需要重新登录';
        window.dispatchEvent(new CustomEvent('auth-requires-logout', { detail: { reason: bodyText, url } }));
      } catch (_) {}
    };

    window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const resp = await _orig(input, init as any);
      if (isRemoteApi(input)) {
        checkResp(resp);
        checkAuth401(input, resp);
      }
      return resp;
    } as typeof window.fetch;
  }
})();

import { showToast } from './toast';

// ====== 数据库代理对象 ======
// 性能优化：sql.js 实例已迁移到主进程，渲染进程通过 IPC 异步调用
// 这样 63MB 数据库的加载和查询不再阻塞 UI 线程，彻底解决白屏问题
interface QueryExecResult { columns: string[]; values: any[][] }

interface DbProxy {
  exec: (sql: string, params?: any[]) => Promise<QueryExecResult[]>;
  run: (sql: string, params?: any[]) => Promise<void>;
  export: () => Promise<Uint8Array>;
}

// ===== 数据库代理对象（已废弃：架构变更为全走服务器 API）=====
// 保留变量避免运行时 undefined 错误，但不再初始化 sql.js
// 给一个空实现，让所有死代码（非 REST 分支）不会报 null 错误
const _NOOP_DB: DbProxy = {
  async exec(_sql: string, _params?: any[]): Promise<{ columns: string[]; values: any[][] }[]> { return []; },
  async run(_sql: string, _params?: any[]): Promise<void> { /* noop */ },
  async export(): Promise<Uint8Array> { return new Uint8Array(0); },
};
let db: DbProxy = _NOOP_DB;
let DB_INITIALIZED = true; // 直接标记已初始化，跳过 sql.js

// Ensure database is initialized before any operation
// 架构变更：所有数据统一走服务器 REST API，此函数为空壳
export async function ensureDBInitialized(): Promise<void> {
  // 不做任何事 —— 所有 CRUD 都走远程 API
}

// Clean empty rows from all tables
export async function cleanEmptyRows(): Promise<Record<string, number>> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const tables = ['influencers', 'influencer_blacklist', 'products', 'orders', 'order_influencers', 'express', 'samples', 'sample_progress'];
  const cleanedCounts: Record<string, number> = {};
  
  for (const table of tables) {
    const results = await db.exec(`SELECT * FROM ${table}`);
    if (results.length === 0 || results[0].values.length === 0) {
      cleanedCounts[table] = 0;
      continue;
    }
    
    const columns = results[0].columns.filter(col => col !== '_id' && col !== 'createdAt' && col !== 'updatedAt');
    const rows = results[0].values;
    const emptyRowIds: string[] = [];
    
    for (const row of rows) {
      const rowObj: Record<string, any> = {};
      results[0].columns.forEach((col, i) => {
        rowObj[col] = row[i];
      });
      
      // Check if all non-system columns are empty
      const isEmpty = columns.every(col => {
        const val = rowObj[col];
        return val === undefined || val === null || val === '' || val === 'null' || val === 'undefined';
      });
      
      if (isEmpty) {
        emptyRowIds.push(rowObj._id);
      }
    }
    
    // Delete empty rows
    for (const id of emptyRowIds) {
      await db.run(`DELETE FROM ${table} WHERE _id = ?`, [id]);
    }
    
    cleanedCounts[table] = emptyRowIds.length;
    console.log(`[清理空行] ${table}: 删除了 ${emptyRowIds.length} 条空行`);
  }
  
  await saveDB();
  return cleanedCounts;
}

// Initialize database via main process IPC (已废弃：全走服务器 API)
export async function initializeLocalDB(): Promise<void> {
  // 架构变更：取消本地 sql.js，所有数据走服务器 REST API
  DB_INITIALIZED = true;
}

// Initialize collections (兼容空壳)
export async function initializeCollections(): Promise<void> {
  // 不做任何事
}

// 数据迁移函数（已废弃：全走远程）
async function migrateImages(): Promise<{ migrated: number; totalKB: number; failed: number }> {
  return { migrated: 0, totalKB: 0, failed: 0 };
}

// Preload all data (for compatibility)
export async function preloadAllData(): Promise<void> {
  console.log('preloadAllData completed');
}

// ===== 4 端统一同步：按主键 _id 全量覆盖写本地库（配合 pullFromRemote 权威拉取） =====
/**
 * 获取本地表实际存在的列（用于过滤后端新增列，避免写入失败）
 */
async function getTableColumns(table: string): Promise<Set<string>> {
  try {
    const result = db!.exec(`PRAGMA table_info("${table}")`);
    if (result.length > 0 && result[0].values.length > 0) {
      const cols = new Set<string>();
      for (const row of result[0].values) {
        if (row.length >= 2) cols.add(String(row[1]));
      }
      if (cols.size > 0) return cols;
    }
  } catch (_) { /* ignore */ }
  // 退避：从 CREATE TABLE 语句解析
  try {
    const result = db!.exec(`SELECT sql FROM sqlite_master WHERE type='table' AND name='${table}'`);
    if (result.length > 0 && result[0].values.length > 0) {
      const createSql: string = result[0].values[0];
      const cols = new Set<string>();
      const matches = createSql.matchAll(/"([^"]+)"/g);
      const SQL_KEYWORDS = new Set(['PRIMARY', 'KEY', 'NOT', 'NULL', 'DEFAULT', 'AUTOINCREMENT', 'INTEGER', 'TEXT', 'REAL', 'BLOB', 'varchar', 'int']);
      for (const m of matches) {
        const col = m[1];
        if (col && !SQL_KEYWORDS.has(col.toUpperCase()) && !SQL_KEYWORDS.has(col.toLowerCase())) {
          cols.add(col);
        }
      }
      if (cols.size > 0) return cols;
    }
  } catch (_) { /* ignore */ }
  console.warn(`[getTableColumns] 无法获取 ${table} 的列信息，将跳过列过滤`);
  return new Set<string>();
}

/**
 * 把 items 全部写入 collection：命中 _id 就 UPDATE，否则 INSERT。
 * - 自动过滤本地表不存在的列，避免后端新增列导致写入失败
 * - 写入结束后统一 saveDB（saveDB 内部 1.5s 防抖，批量写入只会触发一次真实 flush）
 */
export async function overwriteCollection(collection: string, items: any[]): Promise<number> {
  if (!Array.isArray(items) || items.length === 0) return 0;
  await initializeLocalDB();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  // 获取本地表的实际列
  const existingCols = await getTableColumns(collection);
  const canFilter = existingCols.size > 0;
  
  let written = 0;
  let skipped = 0;
  const filterCols = (data: Record<string, unknown>): Record<string, unknown> => {
    if (!canFilter) return data;  // 无法确定列 → 跳过过滤（兼容旧逻辑）
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) {
      if (existingCols.has(k)) result[k] = v;
    }
    return result;
  };
  
  try {
    for (const doc of items) {
      if (!doc || typeof doc !== 'object') continue;
      const id = doc._id;
      const rawData: Record<string, unknown> = { ...doc };
      delete rawData._id;
      const data = filterCols(rawData);
      
      // 如果过滤后没有有效列，跳过
      if (Object.keys(data).length === 0) {
        skipped++;
        continue;
      }
      
      if (id) {
        const existing = db.exec(`SELECT _id FROM "${collection}" WHERE _id = ?`, [id]);
        if (existing.length > 0 && existing[0].values.length > 0) {
          const keys = Object.keys(data);
          if (keys.length > 0) {
            const setClauses = keys.map(k => `"${k}" = ?`).join(', ');
            const values = keys.map(k => data[k]);
            db.run(`UPDATE "${collection}" SET ${setClauses} WHERE _id = ?`, [...values, id]);
          }
          written++;
          continue;
        }
        const allKeys = ['_id', ...Object.keys(data)];
        const placeholders = allKeys.map(() => '?').join(', ');
        const allValues = allKeys.map(k => k === '_id' ? id : data[k]);
        db.run(`INSERT INTO "${collection}" (${allKeys.map(k => `"${k}"`).join(', ')}) VALUES (${placeholders})`, allValues);
        written++;
      } else {
        try { await addDoc(collection, data); written++; } catch (_) { /* ignore */ }
      }
    }
    if (skipped > 0) {
      console.warn(`[overwriteCollection] ${collection} 跳过 ${skipped} 条（本地表缺少对应列）`);
    }
    await saveDB();
    clearCache(collection);
    return written;
  } catch (e) {
    console.error(`[overwriteCollection] ${collection} 写入失败:`, e);
    throw e;
  }
}

// ========== 4 端统一同步：从远程服务器 GET /api/sync/all 拉取全量数据覆盖本地 sql.js ==========
// 商家端后端 GET /api/sync/all 会按 token merchant_id 过滤返回本人数据，
// 保证桌面/Web/PC 三端无论在哪端录入，其他端打开都能看到最新权威数据
// 注意：COL 定义在文件下方，这里用 getter 延迟求值，避免 Vite 代码分割导致 COL 未初始化
const PULL_TABLES_GETTER = () => [
  COL.ORDERS, COL.PRODUCTS, COL.SAMPLES, COL.INFLUENCERS,
  COL.EXPRESS, COL.ORDER_INFLUENCERS, COL.MERCHANTS,
  COL.INFLUENCER_BLACKLIST, COL.EMPLOYEES, COL.WORKGROUPS,
  COL.WORKGROUP_MEMBERS, COL.SAMPLE_PROGRESS, COL.AUDIT_LOGS,
  COL.FEE_LEDGER, COL.WEBHOOK_GROUPS, COL.INFLUENCER_PERFORMANCE,
  COL.WORKGROUP_BUDGETS, COL.SAMPLE_RETURNS, COL.SMS_RECORDS,
];

let LAST_PULL_AT = 0;
const PULL_MIN_INTERVAL = 15 * 1000; // 15 s 内不重复拉，避免并发 fetchAll 压垮接口
let PULL_IN_PROGRESS: Promise<void> | null = null;

export async function pullFromRemote(force = false): Promise<void> {
  await ensureDBInitialized();
  if (PULL_IN_PROGRESS) return PULL_IN_PROGRESS;
  if (!force && Date.now() - LAST_PULL_AT < PULL_MIN_INTERVAL) return;

  const worker = (async () => {
    try {
      const token = await getRemoteToken().catch(() => '');
      if (!token) {
        console.log('[pullFromRemote] 无有效 token，跳过远程拉取（仅读本地库）');
        return;
      }
      const apiBase = REMOTE_API_URL || LOCAL_API_URL;
      if (!apiBase) return;
      const url = apiBase.endsWith('/api')
        ? `${apiBase}/sync/all`
        : `${apiBase}/api/sync/all`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        console.warn(`[pullFromRemote] HTTP ${resp.status} 拉取失败: ${t.substring(0, 120)}`);
        return;
      }
      const payload: any = await resp.json().catch(() => null);
      if (!payload || typeof payload !== 'object') return;
      let total = 0;
      for (const coll of PULL_TABLES_GETTER()) {
        const rows: any[] = Array.isArray(payload[coll]) ? payload[coll] : [];
        // 安全：验证后再清空，避免列不匹配导致数据丢失
        try {
          const existingCols = await getTableColumns(coll);
          const canFilter = existingCols.size > 0;
          
          if (rows.length === 0) {
            continue;  // 空数据不清空本地
          }
          
          // 测试第一条数据是否能写入
          const testDoc = rows[0];
          if (testDoc && canFilter) {
            const testCols = Object.keys(testDoc).filter(k => k === '_id' || existingCols.has(k));
            if (testCols.length <= 1) {
              // 只有 _id 能匹配，说明表结构完全不匹配，跳过
              console.warn(`[pullFromRemote] ${coll} 表结构完全不匹配，跳过本地同步`);
              continue;
            }
          }
          
          // 验证通过，清空旧数据并写入
          try { db?.exec(`DELETE FROM "${coll}"`); } catch (_) { /* 表可能尚未创建，忽略 */ }
          const n = await overwriteCollection(coll, rows);
          total += n;
        } catch (e: any) {
          console.error(`[pullFromRemote] ${coll} 同步失败，跳过:`, e?.message || e);
        }
      }
      if (Array.isArray(payload.settings) && payload.settings.length > 0) {
        try { db?.exec(`DELETE FROM "${COL.SETTINGS}"`); } catch (_) { /* ignore */ }
        const items = payload.settings
          .filter((s: any) => s && typeof s.key === 'string')
          .map((s: any) => ({ key: s.key, value: typeof s.value === 'string' ? s.value : JSON.stringify(s.value) }));
        const n = await overwriteCollection(COL.SETTINGS, items);
        total += n;
      }
      LAST_PULL_AT = Date.now();
      console.log(`✅ [pullFromRemote] 完成：写入 ${total} 条（${PULL_TABLES_GETTER().length + 1} 张表），来源：${apiBase}`);
    } catch (e: any) {
      console.warn('[pullFromRemote] 拉取异常（降级读本地）：', e?.message || e);
    }
  })();
  PULL_IN_PROGRESS = worker;
  try { await worker; } finally { PULL_IN_PROGRESS = null; }
}

// Save database — 通过 IPC 触发主进程防抖保存
// 性能优化：主进程持有 sql.js 实例，防抖保存由主进程管理
// 渲染进程不再需要 export 数据库并通过 IPC 传输 63MB 数据
let saveDBTimer: ReturnType<typeof setTimeout> | null = null;
let saveDBPromise: Promise<void> | null = null;
let saveDBResolve: ((v: void) => void) | null = null;

async function saveDB(): Promise<void> {
  if (!db) return;
  // 如果已有 pending 的保存，直接返回那个 Promise
  if (saveDBPromise) return saveDBPromise;
  // 创建新的 Promise
  saveDBPromise = new Promise<void>((resolve) => { saveDBResolve = resolve; });
  // 防抖：1.5 秒后通过 IPC 触发主进程保存
  if (saveDBTimer) clearTimeout(saveDBTimer);
  saveDBTimer = setTimeout(async () => {
    saveDBTimer = null;
    try {
      await runtime.db.save();
    } catch (e) {
      console.error('[saveDB] 保存失败:', e);
    } finally {
      const r = saveDBResolve;
      saveDBPromise = null;
      saveDBResolve = null;
      if (r) r();
    }
  }, 1500);
  return saveDBPromise;
}

// 强制立即保存（不等防抖）—— 用于关键操作（如删除、退出前）
async function saveDBImmediate(): Promise<void> {
  if (!db) return;
  if (saveDBTimer) { clearTimeout(saveDBTimer); saveDBTimer = null; }
  try {
    await runtime.db.flushSave();
  } catch (e) {
    console.error('[saveDBImmediate] 保存失败:', e);
  } finally {
    const r = saveDBResolve;
    saveDBPromise = null;
    saveDBResolve = null;
    if (r) r();
  }
}

// Generate unique ID
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

// ===== 账户ID生成：4位 0-9/A-Z 大写，排除 I/O =====
const ACCOUNT_ID_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789'.split('');
function _rand4Chars(): string {
  let s = '';
  for (let i = 0; i < 4; i++) s += ACCOUNT_ID_CHARS[Math.floor(Math.random() * ACCOUNT_ID_CHARS.length)];
  return s;
}
/** 商家账户ID：全局唯一（本地仅保证随机冲突重试） */
export async function generateAccountId4(): Promise<string> {
  for (let i = 0; i < 50; i++) {
    const cand = _rand4Chars();
    // 本地轻量检查：若 employees 表已有该 employee_no 则跳过；商家自己账户ID是服务端返回的，本地不走这里
    try {
      if (db) {
        const r = await db.exec("SELECT COUNT(*) AS c FROM employees WHERE employee_no = ?", [cand]);
        if (r.length > 0 && r[0].values.length > 0 && Number(r[0].values[0][0]) > 0) continue;
      }
    } catch (_) {}
    return cand;
  }
  return _rand4Chars();
}
/** 员工账户ID：同商家内唯一（本地按 employees 表查重） */
export async function generateStaffAccountId4(): Promise<string> {
  for (let i = 0; i < 200; i++) {
    const cand = _rand4Chars();
    try {
      if (db) {
        const r = await db.exec("SELECT COUNT(*) AS c FROM employees WHERE employee_no = ?", [cand]);
        if (r.length > 0 && r[0].values.length > 0 && Number(r[0].values[0][0]) > 0) continue;
      }
    } catch (_) {}
    return cand;
  }
  return _rand4Chars();
}

/** 获取当前登录身份的 merchant_id（商家自己 username，员工即所属商家），失败返回 '' */
export function getCurrentUserMerchantId(): string {
  // 兼容三种存储格式：
  // 1) { user: { merchant_id, role, username } }  —— 理论上的旧结构
  // 2) { merchant_id, role, username, employeeId } —— LoginPage 实际写入 auth.ts 存储的结构（扁平 AuthUser）
  // 3) localStorage / sessionStorage 都查一遍
  const stores = [sessionStorage, localStorage];
  for (const store of stores) {
    try {
      const raw = store?.getItem?.('auth_user');
      if (!raw) continue;
      const u = JSON.parse(raw);
      // 方案 A：扁平 AuthUser（LoginPage 实际写入）
      if (u && typeof u === 'object') {
        if (u.merchant_id) return String(u.merchant_id);
        if (u.role === 'merchant' || u.role === 'pending') return String(u.username || '');
        // 方案 B：嵌套 { user: ... } 旧结构
        if (u.user && typeof u.user === 'object') {
          if (u.user.merchant_id) return String(u.user.merchant_id);
          if (u.user.role === 'merchant' || u.user.role === 'pending') return String(u.user.username || '');
        }
      }
    } catch (_) { /* skip */ }
  }
  return '';
}

// Collection names
export const COL = {
  INFLUENCERS: 'shang_influencers',
  INFLUENCER_BLACKLIST: 'influencer_blacklist',
  PRODUCTS: 'shang_products',
  EXPRESS: 'shang_express',
  SAMPLES: 'shang_samples',
  ORDER_INFLUENCERS: 'shang_order_influencers',
  ORDERS: 'shang_orders',
  SETTINGS: 'settings',
  POSTER_TEMPLATE: 'poster_template',
  HANDCARD_TEMPLATE: 'handcard_template',
  EMPLOYEES: 'employees',
  WORKGROUPS: 'workgroups',
  WORKGROUP_MEMBERS: 'workgroup_members',
  PAYMENT_QR: 'payment_qr',
  TRAFFIC_RECORDS: 'traffic_records',
  MERCHANTS: 'merchants',
  SAMPLE_PROGRESS: 'shang_sample_progress',
  AUDIT_LOGS: 'audit_logs',
  FEE_LEDGER: 'fee_ledger',
  WEBHOOK_GROUPS: 'webhook_groups',
  INFLUENCER_PERFORMANCE: 'influencer_performance',
  // 第三期：工作组月度预算
  WORKGROUP_BUDGETS: 'workgroup_budgets',
  // 第三期：样品回收
  SAMPLE_RETURNS: 'shang_sample_returns',
  // 第三期：短信发送记录
  SMS_RECORDS: 'sms_records',
};

// ===== 所有集合统一走服务器 REST API（架构变更：取消本地 sql.js） =====
// 后端 /api/:collection 通用路由已覆盖所有物理表
const REST_COLLECTIONS = new Set([
  COL.ORDERS, COL.PRODUCTS, COL.SAMPLES, COL.INFLUENCERS,
  COL.INFLUENCER_BLACKLIST, COL.EXPRESS, COL.ORDER_INFLUENCERS,
  COL.SAMPLE_PROGRESS, COL.SAMPLE_RETURNS, COL.EMPLOYEES,
  COL.WORKGROUPS, COL.WORKGROUP_MEMBERS, COL.WORKGROUP_BUDGETS,
  COL.AUDIT_LOGS, COL.FEE_LEDGER, COL.WEBHOOK_GROUPS,
  COL.INFLUENCER_PERFORMANCE, COL.SMS_RECORDS,
  COL.SETTINGS, COL.POSTER_TEMPLATE, COL.HANDCARD_TEMPLATE,
  COL.PAYMENT_QR, COL.TRAFFIC_RECORDS, COL.MERCHANTS,
]);
function isRestCollection(collection: string): boolean {
  return REST_COLLECTIONS.has(collection);
}

// ===== 修复问题5: COL物理表名 → 后端API逻辑路由名映射 =====
// 前端 COL 常量用物理表名（如 shang_products），但后端注册的路由是逻辑名（/api/products）
// 此函数把 shang_* 前缀去掉，匹配后端 tables.forEach 注册的路由
function collectionToApiPath(collection: string): string {
  // shang_products → products, shang_samples → samples, etc.
  if (collection.startsWith('shang_')) return collection.substring(6);
  if (collection.startsWith('guan_')) return collection.substring(5);
  return collection;
}

// ===== P2-O3: 全模块审计日志 - 全局上下文 =====
let _auditOperatorId: string = '';
let _auditOperatorName: string = '';

/**
 * 设置当前审计操作人（登录成功后/切换用户时调用）
 */
export function setAuditOperator(operatorId: string, operatorName: string): void {
  _auditOperatorId = operatorId || '';
  _auditOperatorName = operatorName || '';
  console.log(`[审计] 当前操作人已设置: ${operatorName} (${operatorId || '未知ID'})`);
}

/** 获取当前审计操作人 */
export function getAuditOperator(): { id: string; name: string } {
  return { id: _auditOperatorId, name: _auditOperatorName };
}

/**
 * 需要审计的 collection → action 映射
 * 未在此列表中的表（如 audit_logs/settings/内部表）跳过审计
 */
const AUDIT_ACTION_MAP: Record<string, { create?: string; edit?: string; delete?: string }> = {
  [COL.EMPLOYEES]: {
    create: 'employee_create',
    edit: 'employee_edit',
    delete: 'employee_delete'
  },
  [COL.INFLUENCERS]: {
    create: 'influencer_create',
    edit: 'influencer_edit',
    delete: 'influencer_delete'
  },
  [COL.INFLUENCER_BLACKLIST]: {
    create: 'influencer_blacklist',
    delete: 'influencer_unblacklist'
  },
  [COL.PRODUCTS]: {
    create: 'product_create',
    edit: 'product_edit',
    delete: 'product_delete'
  },
  [COL.SAMPLES]: {
    create: 'sample_create',
    edit: 'sample_edit',
    delete: 'sample_delete'
  },
  [COL.EXPRESS]: {
    create: 'express_create',
    edit: 'express_edit',
    delete: 'express_delete'
  },
  [COL.WORKGROUPS]: {
    create: 'workgroup_create',
    edit: 'workgroup_edit',
    delete: 'workgroup_delete'
  },
  [COL.WEBHOOK_GROUPS]: {
    create: 'webhook_group_create',
    edit: 'webhook_group_edit',
    delete: 'webhook_group_delete'
  }
};

// ===== P2-O5: 素材归档 =====
/** 支持归档的表 */
const ARCHIVABLE_COLLECTIONS = new Set([
  COL.INFLUENCERS, COL.PRODUCTS, COL.EXPRESS, COL.SAMPLES
]);

/**
 * 将 records 标记为已归档 / 撤销归档（批量）
 * 写入对应 product_archive / influencer_archive / express_archive / sample_archive 审计
 */
export async function setDocsArchived(
  collection: string,
  ids: string[],
  archived: boolean
): Promise<{ count: number }> {
  if (!ARCHIVABLE_COLLECTIONS.has(collection)) {
    throw new Error(`${collection} 不支持归档操作`);
  }
  await ensureDBInitialized();
  if (!db || ids.length === 0) return { count: 0 };
  const now = new Date().toISOString();
  const flag = archived ? 1 : 0;
  const colName = collection.replace(COL.INFLUENCERS, 'influencer')
    .replace(COL.PRODUCTS, 'product')
    .replace(COL.EXPRESS, 'express')
    .replace(COL.SAMPLES, 'sample');
  const action = archived ? `${colName}_archive` : `${colName}_unarchive`;

  for (const id of ids) {
    // 归档前取快照用于审计
    let snapshot = '';
    try {
      const res = await db.exec(`SELECT * FROM ${collection} WHERE _id = ?`, [id]);
      if (res.length > 0 && res[0].values.length > 0) {
        const obj: Record<string, unknown> = {};
        res[0].columns.forEach((c, i) => { obj[c] = res[0].values[0][i]; });
        snapshot = JSON.stringify(obj);
      }
    } catch (_) { /* ignore */ }

    await db.run(`UPDATE ${collection} SET archived = ?, updatedAt = ? WHERE _id = ?`, [flag, now, id]);

    // 审计
    if ((_auditOperatorId || _auditOperatorName) && snapshot) {
      try {
        const logId = generateId();
        await db.run(
          `INSERT INTO audit_logs (_id, action, operator_id, operator_name, target_id, target_snapshot, reason, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [logId, action, _auditOperatorId, _auditOperatorName, id, snapshot, '无', now]
        );
      } catch (_) { /* ignore */ }
    }
  }
  await saveDB();
  clearCache(collection);
  return { count: ids.length };
}

/**
 * 核心 CRUD 操作后自动写审计日志
 * sample_progress_delete 走专用 deleteSampleProgressWithAudit（保留原逻辑）
 */
async function tryWriteAuditForCRUD(
  op: 'create' | 'edit' | 'delete',
  collection: string,
  targetId: string,
  snapshot: Record<string, unknown> | string
): Promise<void> {
  try {
    // 1. 跳过不需要审计的表
    const mapping = AUDIT_ACTION_MAP[collection];
    if (!mapping) return;
    const action = mapping[op];
    if (!action) return;

    // 2. 没有操作人则跳过（未登录场景）
    if (!_auditOperatorId && !_auditOperatorName) return;

    // 3. 快照转字符串
    const snapshotStr = typeof snapshot === 'string' ? snapshot : JSON.stringify(snapshot);

    // 4. 直接插入（复用 addAuditLog 的 SQL 但避免重复 saveDB，此处内联）
    if (!db) return;
    const logId = generateId();
    const now = new Date().toISOString();
    await db.run(
      `INSERT INTO audit_logs (_id, action, operator_id, operator_name, target_id, target_snapshot, reason, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [logId, action, _auditOperatorId, _auditOperatorName, targetId, snapshotStr, '无', now]
    );
    console.log(`[审计] ${action} → ${collection}#${targetId.slice(0, 8)}... by ${_auditOperatorName}`);
  } catch (e) {
    // 审计失败不能影响主流程
    console.warn('[审计] 写入失败（已忽略）:', e);
  }
}

// 简单的内存缓存
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

interface FetchAllOptions {
  skipCache?: boolean;
  /** 是否包含已归档数据（仅对 4 张可归档表生效），默认 false = 仅显示未归档 */
  showArchived?: boolean;
  /** 限制返回行数（性能优化：63MB 数据库全表扫描很慢），默认 0 = 不限制 */
  limit?: number;
}

/**
 * 获取表中所有记录
 * @param collection 表名
 * @param conditions 精确匹配条件（可选）
 * @param optionsOrSkip boolean=skipCache 兼容旧签名；对象=新 options
 */
export async function fetchAll<T>(
  collection: string,
  conditions?: Record<string, unknown>,
  optionsOrSkip: boolean | FetchAllOptions = false
): Promise<T[]> {
  // 解析参数
  const opts: FetchAllOptions = typeof optionsOrSkip === 'boolean'
    ? { skipCache: optionsOrSkip }
    : optionsOrSkip;
  const skipCache = !!opts.skipCache;
  const showArchived = !!opts.showArchived;

  // P2-O5: 归档过滤函数（可归档表 + 未显示归档时，剔除 archived=1）
  const filterArchived = <R extends any>(arr: R[]): R[] => {
    if (showArchived) return arr;
    if (!ARCHIVABLE_COLLECTIONS.has(collection)) return arr;
    return arr.filter(item => {
      const a = (item as any)?.archived;
      return a !== 1 && a !== '1';
    });
  };

  // 如果 conditions 是空对象（conditions={}），等价于无查询条件
  const hasConditions = !!(conditions && Object.keys(conditions).length > 0);

  // 构造全局唯一缓存键
  const condFragment = hasConditions
    ? JSON.stringify(Object.fromEntries(Object.entries(conditions!).sort(([a], [b]) => a.localeCompare(b))))
    : '';
  const limitFragment = opts.limit && opts.limit > 0 ? `limit${opts.limit}` : '';
  const cacheKey = [
    collection,
    condFragment,
    showArchived ? 'archived' : '',
    limitFragment
  ].filter(Boolean).join('::');

  // 有缓存且不跳过 → 直接命中
  if (!skipCache) {
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`📦 使用内存缓存: ${cacheKey}`);
      return cached.data;
    }
  }

  let data: T[] = [];
  let apiSucceeded = false;

  // ===== 核心改造：REST 集合直接走服务器 API，不再经过本地 sql.js =====
  if (isRestCollection(collection)) {
    const token = await getRemoteToken().catch(() => '');
    const apiBase = getRemoteApiUrl();
    const apiPath = collectionToApiPath(collection);
    if (!token) {
      console.warn(`[fetchAll] ${collection} 无 token，跳过服务器拉取，尝试读缓存或本地`);
    } else {
      try {
        const resp = await fetch(`${apiBase}/${apiPath}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json', ...getAppBuildHeaders() }
        });
        if (resp.ok) {
          const result = await resp.json().catch(() => []);
          data = Array.isArray(result) ? result : [];
          apiSucceeded = true;
          // 内存中过滤 conditions
          if (hasConditions) {
            data = data.filter((item: any) => {
              for (const [key, value] of Object.entries(conditions!)) {
                if (item[key] != value) return false; // eslint-disable-line eqeqeq
              }
              return true;
            });
          }
          // 内存中 limit
          if (opts.limit && opts.limit > 0) {
            data = data.slice(0, opts.limit);
          }
        } else {
          console.warn(`[fetchAll] ${collection} API 失败 ${resp.status}，降级读本地或返回空数组`);
        }
      } catch (e) {
        console.warn(`[fetchAll] ${collection} API 异常:`, (e as Error).message);
      }
    }
  }

  // 非 REST 集合 或 API 失败/无 token → 读本地 sql.js
  if (!apiSucceeded && !isRestCollection(collection)) {
    if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
    const whereParts: string[] = [];
    const params: unknown[] = [];
    if (hasConditions) {
      for (const [key, value] of Object.entries(conditions!)) {
        whereParts.push(`${key} = ?`);
        params.push(value);
      }
    }
    const limitClause = opts.limit && opts.limit > 0 ? ` LIMIT ${opts.limit}` : '';
    const sql = `SELECT * FROM ${collection}${whereParts.length ? ' WHERE ' + whereParts.join(' AND ') : ''} ORDER BY createdAt DESC${limitClause}`;
    const raw = params.length ? await db.exec(sql, params) : await db.exec(sql);
    if (raw.length > 0 && raw[0].values.length > 0) {
      const columns = raw[0].columns;
      data = raw[0].values.map((row: unknown[]) => {
        const obj: any = {};
        columns.forEach((col, i) => { obj[col] = row[i]; });
        return obj as T;
      });
    }
  }

  // 归档过滤
  data = filterArchived(data);

  // 脏图片字段兜底过滤：避免脏数据（如 "/x.jpg"、"/login-bcmain.webp" 等）导致每次页面打开都出 404
  const IMG_KEYS = ['mainImage', 'douyinQR', 'kuaishouQR', 'shipinhaoQR', 'qrImage', 'image', 'bgImage', 'avatar'];
  const safeImg = (s: unknown) => {
    if (typeof s !== 'string' || !s) return s;
    if (s.startsWith('data:') || s.startsWith('http') || s.startsWith('blob:')) return s;
    if (s.startsWith('images/') || s.startsWith('/uploads/') || s.startsWith('/assets/') || s.startsWith('/static/')) return s;
    if (/^\/login-bg-main\.(png|jpg|jpeg|webp)$/.test(s)) return s;
    if (/^\/[a-zA-Z0-9_\-]{3,}\.(png|jpg|jpeg|webp|gif|svg)$/.test(s)) return s;
    console.warn(`[fetchAll] 脏图片路径已清除: ${s.substring(0, 160)}`);
    return '';
  };
  if (IMG_KEYS.some(k => collection.includes(k)) || data.length) {
    data = data.map(row => {
      if (row && typeof row === 'object') {
        const obj: any = row;
        let changed = false;
        for (const k of IMG_KEYS) {
          if (k in obj) {
            const cleaned = safeImg(obj[k]);
            if (cleaned !== obj[k]) { obj[k] = cleaned; changed = true; }
          }
        }
        return changed ? { ...obj } : row;
      }
      return row;
    });
  }

  // 写入内存缓存
  cache.set(cacheKey, { data, timestamp: Date.now() });
  if (!hasConditions && !opts.limit) {
    cache.set(collection, { data, timestamp: Date.now() });
  }
  return data;
}

// ========== 性能优化：SQL 聚合查询（替代 fetchAll + JS filter，避免读取图片 BLOB） ==========

/**
 * SQL 级 COUNT 查询：直接返回数量，不读行数据，不触发图片 BLOB 解析
 * 支持多条件 AND 组合，WHERE 下推到 SQL 层
 */
export async function dbCount(
  collection: string,
  conditions?: Record<string, unknown>
): Promise<number> {
  // ===== 核心改造：REST 集合直接走 API 列表后计数，不再读本地 sql.js =====
  if (isRestCollection(collection)) {
    try {
      const all = await fetchAll<Record<string, unknown>>(collection, conditions, { skipCache: true });
      return all.length;
    } catch (e) {
      if (runtime.isWeb) throw e;
      console.warn(`[dbCount] ${collection} API 计数失败，降级读本地:`, (e as Error).message);
    }
  }

  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const whereParts: string[] = [];
  const params: unknown[] = [];
  if (conditions) {
    for (const [key, value] of Object.entries(conditions)) {
      if (value === null || value === undefined) continue;
      whereParts.push(`${key} = ?`);
      params.push(value);
    }
  }
  const sql = `SELECT COUNT(*) as c FROM ${collection}${whereParts.length ? ' WHERE ' + whereParts.join(' AND ') : ''}`;
  const raw = params.length ? await db.exec(sql, params) : await db.exec(sql);
  if (raw.length === 0 || raw[0].values.length === 0) return 0;
  return Number(raw[0].values[0][0]) || 0;
}

/**
 * 自定义 WHERE 条件 COUNT：支持复杂表达式（IS NULL、IN、LIKE、比较等）
 * 例：countWhere('express', 'signTime IS NULL AND status != ?', ['已签收'])
 */
export async function countWhere(
  collection: string,
  whereExpr: string,
  whereParams: unknown[] = []
): Promise<number> {
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const sql = `SELECT COUNT(*) as c FROM ${collection}${whereExpr ? ` WHERE ${whereExpr}` : ''}`;
  const raw = whereParams.length ? await db.exec(sql, whereParams) : await db.exec(sql);
  if (raw.length === 0 || raw[0].values.length === 0) return 0;
  return Number(raw[0].values[0][0]) || 0;
}

/**
 * GROUP BY 分组统计：返回 [{groupKey: 'xxx', count: N}, ...]
 * 例：groupCount('samples', 'approval_status') → [{approval_status: 'pending', count: 5}, ...]
 */
export async function groupCount(
  collection: string,
  groupColumn: string,
  whereExpr?: string,
  whereParams: unknown[] = []
): Promise<Array<Record<string, unknown>>> {
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const sql = `SELECT ${groupColumn}, COUNT(*) as cnt FROM ${collection}${whereExpr ? ` WHERE ${whereExpr}` : ''} GROUP BY ${groupColumn}`;
  const raw = whereParams.length ? await db.exec(sql, whereParams) : await db.exec(sql);
  if (raw.length === 0) return [];
  const cols = raw[0].columns;
  return raw[0].values.map((row: unknown[]) => {
    const obj: Record<string, unknown> = {};
    cols.forEach((c, i) => { obj[c] = row[i]; });
    obj.count = obj.cnt; // 重命名 cnt → count 更易读
    delete obj.cnt;
    return obj;
  });
}

/**
 * 只查询指定字段，不 SELECT *（避免读取图片 BLOB）
 * 例：queryFields('products', ['_id', 'name', 'shortName', 'price'])
 *     queryFields('samples', ['_id', 'recordDate'], {approval_status: 'pending'})
 */
export async function queryFields<T = Record<string, unknown>>(
  collection: string,
  fields: string[],
  conditions?: Record<string, unknown>,
  options: { limit?: number; orderBy?: string; showArchived?: boolean; skipCache?: boolean } = {}
): Promise<T[]> {
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const { limit, orderBy = 'createdAt DESC', showArchived = false, skipCache = false } = options;
  const fieldStr = fields.join(', ');

  // WHERE 条件
  const whereParts: string[] = [];
  const params: unknown[] = [];
  if (conditions) {
    for (const [key, value] of Object.entries(conditions)) {
      whereParts.push(`${key} = ?`);
      params.push(value);
    }
  }
  // 归档过滤（SQL 下推，避免 JS 层过滤）
  if (!showArchived && ARCHIVABLE_COLLECTIONS.has(collection)) {
    whereParts.push(`(archived IS NULL OR archived != 1)`);
  }
  const limitClause = limit && limit > 0 ? ` LIMIT ${limit}` : '';
  const sql = `SELECT ${fieldStr} FROM ${collection}${whereParts.length ? ' WHERE ' + whereParts.join(' AND ') : ''} ORDER BY ${orderBy}${limitClause}`;

  // 缓存键
  const cacheKey = [
    'fields',
    collection,
    fieldStr,
    conditions ? JSON.stringify(conditions) : '',
    showArchived ? 'archived' : '',
    limit ? `limit${limit}` : ''
  ].join('::');
  if (!skipCache) {
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`📦 使用字段缓存: ${cacheKey}`);
      return cached.data as T[];
    }
  }

  const raw = params.length ? await db.exec(sql, params) : await db.exec(sql);
  if (raw.length === 0) return [];
  const cols = raw[0].columns;
  const data = raw[0].values.map((row: unknown[]) => {
    const obj: Record<string, unknown> = {};
    cols.forEach((c, i) => { obj[c] = row[i]; });
    return obj as T;
  });

  cache.set(cacheKey, { data, timestamp: Date.now() });
  return data;
}

/**
 * 执行自定义 SELECT SQL，返回结果集（与 fetchAll 格式一致）
 * 慎用：只用于首页/列表页的统计查询，避免 SQL 注入
 */
export async function rawQuery<T = Record<string, unknown>>(
  sql: string,
  params: unknown[] = []
): Promise<T[]> {
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const raw = params.length ? await db.exec(sql, params) : await db.exec(sql);
  if (raw.length === 0) return [];
  const cols = raw[0].columns;
  return raw[0].values.map((row: unknown[]) => {
    const obj: Record<string, unknown> = {};
    cols.forEach((c, i) => { obj[c] = row[i]; });
    return obj as T;
  });
}

export function getCachedData<T>(collection: string): T[] | null {
  const cached = cache.get(collection);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data as T[];
  }
  return null;
}

/** 手动写入纯 collection 键缓存（配合 queryFields 使用，让 getCachedData 能命中） */
export function setCachedData<T>(collection: string, data: T[]): void {
  cache.set(collection, { data, timestamp: Date.now() });
}

export async function count(collection: string): Promise<number> {
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const results = await db.exec(`SELECT COUNT(*) as count FROM ${collection}`);
  if (results.length === 0 || results[0].values.length === 0) return 0;
  return Number(results[0].values[0][0]) || 0;
}

/**
 * 商家端首页聚合接口：1 次请求替代原 loadData 30+ 次请求
 * 返回结构含：today / todo / express / weeklyTrend / monthTotal / lastMonthTotal /
 *              influencerStats / employeeStats / orderStats / thisMonthSamples / thisMonthExpressCnt /
 *              topLists / influencers / products / extra / _elapsedMs
 * 若接口不可达（如后端未升级）则返回 null，调用方 fallback 到原多请求逻辑。
 */
export async function fetchHomeStats(): Promise<any | null> {
  const token = await getRemoteToken().catch(() => '');
  if (!token || !REMOTE_API_URL) return null;
  try {
    const resp = await fetch(`${REMOTE_API_URL}/home/stats`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json', ...getAppBuildHeaders() },
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    if (!json || typeof json !== 'object') return null;
    return json;
  } catch (e) {
    console.warn('[shop fetchHomeStats] 聚合接口不可达，降级走原多请求逻辑：', (e as any)?.message);
    return null;
  }
}

// ========== 图片服务器存储辅助函数（解决多端同步问题） ==========
// 图片上传到服务器 uploads/ 目录，DB 里存 URL（/uploads/products/xxx.png）
// 所有客户端通过 HTTP URL 访问图片，不依赖本地文件系统

/** 判断字符串是不是服务器图片 URL（/uploads/xxx 或完整 http URL） */
function isImageUrl(s: unknown): boolean {
  return typeof s === 'string' && (s.startsWith('/uploads/') || s.startsWith('http'));
}

/**
 * 从 DB 字段读取图片，兼容三种模式：
 * - 新模式：字段是 /uploads/products/xxx.png → 拼成完整 URL 返回
 * - 旧模式（遗留）：字段是 images/xxx 路径 → 返回空（本地文件方案已废弃）
 * - 旧模式：字段是 base64 / data:image/xxx → 直接返回
 * 空/非法值返回空字符串
 */
export async function readDbImage(rawValue: unknown): Promise<string> {
  if (!rawValue || typeof rawValue !== 'string') return '';
  if (rawValue.startsWith('http')) return rawValue;
  if (rawValue.startsWith('/uploads/')) return `${REMOTE_API_URL}${rawValue}`;
  if (rawValue.startsWith('data:image/')) return rawValue;
  if (rawValue.startsWith('file://')) return rawValue;
  // Vite 构建后的本地资源路径（/assets/xxx.png 等）
  if (rawValue.startsWith('/assets/') || rawValue.startsWith('/favicon')) return rawValue;
  // 纯 base64，补一个前缀（推断 png）
  if (rawValue.length > 1000 && /^[A-Za-z0-9+/=]+$/.test(rawValue)) {
    return `data:image/png;base64,${rawValue}`;
  }
  return '';
}

/**
 * 上传图片到服务器，返回 URL（/uploads/products/xxx.png）
 * 用在 ProductsPage 新增/编辑 产品、PosterPage 保存背景图时
 */
export async function saveImageAndGetPath(
  category: 'products' | 'poster',
  filename: string,
  dataURL: string
): Promise<string> {
  const token = await getRemoteToken();
  const resp = await fetch(`${REMOTE_API_URL}/upload-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ category, filename, data: dataURL }),
  });
  const r = await resp.json();
  if (!r?.success) throw new Error(r?.error || '上传图片失败');
  return r.url as string;
}

/** 删除服务器上的图片文件（产品/模板被删除时清理） */
export async function deleteImageFile(url: string): Promise<void> {
  if (!isImageUrl(url) || !url.includes('/uploads/')) return;
  try {
    const token = await getRemoteToken();
    await fetch(`${REMOTE_API_URL}/delete-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ url }),
    });
  } catch (e) {
    console.warn('[deleteImageFile] 删除服务器图片失败:', e);
  }
}

/**
 * 数据库迁移函数：将 products / poster_template 表里的图片 BLOB 上传到服务器
 * 1. 读出每一行的 mainImage / douyinQR / kuaishouQR / shipinhaoQR / bgImage
 * 2. 如果是 base64 形式 → 上传到服务器，返回 /uploads/products/{_id}_{type}.png
 * 3. 更新 DB 字段为 URL，把 BLOB 清空，数据库体积从 63MB → 约 2MB
 * 幂等：字段已经是 /uploads/ URL 的跳过；settings 中 image_migrated=done 时整体跳过
 */
export async function migrateImagesToFiles(): Promise<{ migrated: number; totalKB: number }> {
  if (!db) return { migrated: 0, totalKB: 0 };

  // ====== 关键修复：不再依赖 settings.image_migrated 标记 ======
  // 之前Bug：即使上传失败也会标记 done，导致 DB 永远 65MB
  // 现在：直接检查 products 表是否还有大 BLOB，有就迁移
  let needsMigration = false;
  try {
    // 检查 products 表是否还有 base64 大字段（>1KB 视为未迁移）
    const r = await db.exec(`SELECT COUNT(*) as cnt FROM ${COL.PRODUCTS} WHERE 
      (mainImage IS NOT NULL AND mainImage NOT LIKE '/uploads/%' AND mainImage NOT LIKE 'http%' AND length(mainImage) > 500)
      OR (douyinQR IS NOT NULL AND douyinQR NOT LIKE '/uploads/%' AND douyinQR NOT LIKE 'http%' AND length(douyinQR) > 500)
      OR (kuaishouQR IS NOT NULL AND kuaishouQR NOT LIKE '/uploads/%' AND kuaishouQR NOT LIKE 'http%' AND length(kuaishouQR) > 500)
      OR (shipinhaoQR IS NOT NULL AND shipinhaoQR NOT LIKE '/uploads/%' AND shipinhaoQR NOT LIKE 'http%' AND length(shipinhaoQR) > 500)
    `);
    if (r.length && r[0].values.length) {
      needsMigration = Number(r[0].values[0][0]) > 0;
    }
  } catch (_) {}

  if (!needsMigration) {
    // 检查 poster/handcard 模板表
    for (const colName of [COL.POSTER_TEMPLATE, COL.HANDCARD_TEMPLATE]) {
      try {
        const r = await db.exec(`SELECT COUNT(*) as cnt FROM ${colName} WHERE 
          bgImage IS NOT NULL AND bgImage NOT LIKE '/uploads/%' AND bgImage NOT LIKE 'http%' AND length(bgImage) > 500
        `);
        if (r.length && r[0].values.length && Number(r[0].values[0][0]) > 0) {
          needsMigration = true;
          break;
        }
      } catch (_) {}
    }
  }

  if (!needsMigration) {
    console.log('[Migrate] 无大 BLOB 待迁移，跳过');
    return { migrated: 0, totalKB: 0 };
  }

  const startAt = Date.now();
  let migrated = 0;
  let failed = 0;
  let totalKB = 0;

  console.log('[Migrate] 检测到大 BLOB，开始迁移图片 → 服务器...');

  // === 1. products 表：mainImage / douyinQR / kuaishouQR / shipinhaoQR ===
  try {
    const prodRows = await db.exec(`SELECT _id, mainImage, douyinQR, kuaishouQR, shipinhaoQR FROM ${COL.PRODUCTS}`);
    if (prodRows.length && prodRows[0].values.length) {
      const cols = prodRows[0].columns;
      const idx = {
        _id: cols.indexOf('_id'),
        main: cols.indexOf('mainImage'),
        dy: cols.indexOf('douyinQR'),
        ks: cols.indexOf('kuaishouQR'),
        sph: cols.indexOf('shipinhaoQR'),
      };
      for (const row of prodRows[0].values) {
        const id = String(row[idx._id] || '');
        if (!id) continue;
        const fields: Array<{ colName: string; type: string; value: any }> = [
          { colName: 'mainImage', type: 'main', value: row[idx.main] },
          { colName: 'douyinQR', type: 'douyin', value: row[idx.dy] },
          { colName: 'kuaishouQR', type: 'kuaishou', value: row[idx.ks] },
          { colName: 'shipinhaoQR', type: 'shipinhao', value: row[idx.sph] },
        ];
        for (const f of fields) {
          if (!f.value || typeof f.value !== 'string') continue;
          // 已经是 URL 或路径就跳过
          if (f.value.startsWith('/uploads/') || f.value.startsWith('http')) continue;
          // 必须是看起来像图片内容的
          let dataURL = f.value;
          if (!dataURL.startsWith('data:image/')) {
            if (dataURL.length < 500 || !/^[A-Za-z0-9+/=\s]+$/.test(dataURL)) continue;
            dataURL = `data:image/png;base64,${dataURL}`;
          }
          try {
            const fileName = `${id}_${f.type}.png`;
            const url = await saveImageAndGetPath('products', fileName, dataURL);
            await db.run(`UPDATE ${COL.PRODUCTS} SET ${f.colName} = ? WHERE _id = ?`, [url, id]);
            migrated++;
            totalKB += Math.round(dataURL.length * 0.75 / 1024);
          } catch (e) {
            failed++;
            console.warn(`[Migrate] products#${id} ${f.colName} 失败:`, (e as Error).message);
          }
        }
      }
    }
  } catch (e) {
    console.error('[Migrate] products 迁移失败:', e);
  }

  // === 2. poster_template / handcard_template 表：bgImage ===
  for (const colName of [COL.POSTER_TEMPLATE, COL.HANDCARD_TEMPLATE]) {
    try {
      const rows = await db.exec(`SELECT _id, bgImage FROM ${colName}`);
      if (rows.length && rows[0].values.length) {
        const cols = rows[0].columns;
        const idIdx = cols.indexOf('_id');
        const bgIdx = cols.indexOf('bgImage');
        for (const row of rows[0].values) {
          const id = String(row[idIdx] || '');
          const bg = row[bgIdx];
          if (!id || !bg || typeof bg !== 'string') continue;
          if (bg.startsWith('/uploads/') || bg.startsWith('http')) continue;
          let dataURL = bg;
          if (!dataURL.startsWith('data:image/')) {
            if (dataURL.length < 500) continue;
            dataURL = `data:image/png;base64,${dataURL}`;
          }
          try {
            const fileName = `${id}_bgImage.png`;
            const url = await saveImageAndGetPath('poster', fileName, dataURL);
            await db.run(`UPDATE ${colName} SET bgImage = ? WHERE _id = ?`, [url, id]);
            migrated++;
            totalKB += Math.round(dataURL.length * 0.75 / 1024);
          } catch (e) {
            failed++;
            console.warn(`[Migrate] ${colName}#${id} bgImage 失败:`, (e as Error).message);
          }
        }
      }
    } catch (e) {
      // 表不存在等可忽略
    }
  }

  // === 3. 只在迁移成功（无失败）时 VACUUM + 标记完成 ===
  // 关键修复：之前即使全部失败也标记 done，导致 DB 永远 65MB
  if (migrated > 0 && failed === 0) {
    // VACUUM 回收因 BLOB 清空而产生的空闲页
    try {
      console.log('[Migrate] VACUUM 回收空间...');
      const t0 = Date.now();
      await db.run('VACUUM');
      console.log(`[Migrate] VACUUM 完成，耗时 ${Date.now() - t0}ms`);
    } catch (e) {
      console.warn('[Migrate] VACUUM 失败（可忽略）:', (e as Error).message);
    }
  } else if (failed > 0) {
    console.warn(`[Migrate] ${failed} 张图片迁移失败，不标记完成，下次启动会重试`);
  }

  // 强制保存数据库（触发主进程 flush save）
  try { await runtime.db.flushSave(); } catch (_) {}

  clearCache();
  const seconds = ((Date.now() - startAt) / 1000).toFixed(1);
  console.log(`[Migrate] 迁移完成：成功 ${migrated} 张, 失败 ${failed} 张, ${(totalKB / 1024).toFixed(2)} MB, 耗时 ${seconds}s`);
  return { migrated, totalKB };
}

export async function silentRefresh<T>(collection: string, limit?: number): Promise<T[]> {
  // 刷新按钮触发：强制跳过内存缓存，直接从 API 拉最新数据
  const result = await fetchAll<T>(collection, undefined, { skipCache: true, limit });
  console.log(`[silentRefresh] ${collection}: ${result.length} 条数据${limit ? ` (limit=${limit})` : ''}`);
  return result;
}

export function clearCache(collection?: string) {
  if (collection) {
    for (const key of cache.keys()) {
      if (key.startsWith(collection)) {
        cache.delete(key);
      }
    }
  } else {
    cache.clear();
  }
  console.log('🧹 缓存已清除');
}

// 把新记录追加到该 collection 的所有缓存中（避免清空缓存触发重渲染）
function appendToCache(collection: string, doc: Record<string, unknown>) {
  let matched = false;
  for (const [key, cached] of cache.entries()) {
    if (!key.startsWith(collection)) continue;
    if (!Array.isArray(cached.data)) continue;
    const exists = cached.data.find((item: any) => item._id === doc._id);
    if (!exists) {
      cached.data.unshift(doc);
    }
    matched = true;
  }
  // 兜底：若 collection 缓存完全不存在，创建一个
  if (!matched) {
    const existing = cache.get(collection);
    if (existing && Array.isArray(existing.data)) {
      const exists = existing.data.find((item: any) => item._id === doc._id);
      if (!exists) existing.data.unshift(doc);
    } else {
      cache.set(collection, { data: [doc], timestamp: Date.now() });
      console.log(`[appendToCache] 创建 ${collection} 新缓存 key, 初始1条`);
    }
  }
}

// 更新缓存中指定 _id 的记录
function updateCacheById(collection: string, id: string, patch: Record<string, unknown>) {
  for (const [key, cached] of cache.entries()) {
    if (!key.startsWith(collection)) continue;
    if (!Array.isArray(cached.data)) continue;
    const idx = cached.data.findIndex((item: any) => item._id === id);
    if (idx >= 0) {
      cached.data[idx] = { ...cached.data[idx], ...patch };
    }
  }
}

// 从缓存中移除指定 _id 的记录
function removeFromCache(collection: string, id: string) {
  for (const [key, cached] of cache.entries()) {
    if (!key.startsWith(collection)) continue;
    if (!Array.isArray(cached.data)) continue;
    cached.data = cached.data.filter((item: any) => item._id !== id);
  }
}

// Add a document
export async function addDoc(collection: string, data: Record<string, unknown>) {
  const id = generateId();
  const now = new Date().toISOString();
  const doc: Record<string, unknown> = { _id: id, ...data, createdAt: now, updatedAt: now };
  // 数据隔离：注入当前商家 merchant_id（员工登录即所属商家）
  if (doc.merchant_id === undefined || doc.merchant_id === null || doc.merchant_id === '') {
    const mid = getCurrentUserMerchantId();
    if (mid) doc.merchant_id = mid;
  }

  // ===== 核心改造：REST 集合直接 POST 服务器，不再写本地 sql.js =====
  if (isRestCollection(collection)) {
    const token = await getRemoteToken().catch(() => '');
    const apiBase = getRemoteApiUrl();
    if (!token || !apiBase) {
      throw new Error('无可用登录令牌，请先登录');
    }
    const resp = await fetch(`${apiBase}/${collectionToApiPath(collection)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(doc),
    });
    if (!resp.ok) {
      const errText = (await resp.text()).substring(0, 200);
      throw new Error(`服务器写入失败 ${resp.status}: ${errText}`);
    }
    const result = await resp.json().catch(() => ({}));
    if (result && result._id) doc._id = result._id;

    // 只把新记录追加到内存缓存，不清空整个 collection（避免页面重新渲染死循环）
    appendToCache(collection, doc);

    if (collection === COL.EXPRESS) {
      sendExpressSMS(doc, 'create').catch(e => console.error('[addDoc] 快递短信发送失败:', e));
    }
    return { id: doc._id };
  }

  // 非 REST 集合：保留本地写入（settings/poster_template 等）
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const cols = Object.keys(doc).join(', ');
  const placeholders = Object.keys(doc).map(() => '?').join(', ');
  const values = Object.values(doc).map(v => String(v ?? ''));
  await db.run(`INSERT INTO ${collection} (${cols}) VALUES (${placeholders})`, values);

  // P2-O3: CREATE 审计
  await tryWriteAuditForCRUD('create', collection, id, doc);

  await saveDB();
  // 非 REST 集合才需要清缓存，且只清该 collection
  clearCache(collection);

  if (collection === COL.EXPRESS) {
    sendExpressSMS(doc, 'create').catch(e => console.error('[addDoc] 快递短信发送失败:', e));
  }

  return { id: doc._id };
}

// Bulk add/overwrite documents
export async function bulkAddDocs(collection: string, items: Record<string, unknown>[], overwrite = false) {
  const now = new Date().toISOString();
  const globalMid = getCurrentUserMerchantId();

  const docs: Record<string, unknown>[] = [];
  for (const data of items) {
    const id = data._id || generateId();
    const doc: Record<string, unknown> = { _id: id, ...data, createdAt: data.createdAt || now, updatedAt: now };
    if (doc.merchant_id === undefined || doc.merchant_id === null || doc.merchant_id === '') {
      if (globalMid) doc.merchant_id = globalMid;
    }
    docs.push(doc);
  }

  // ===== 核心改造：REST 集合直接 POST 服务器 =====
  if (isRestCollection(collection)) {
    const token = await getRemoteToken().catch(() => '');
    const apiBase = getRemoteApiUrl();
    if (!token || !apiBase) {
      throw new Error('无可用登录令牌，请先登录');
    }

    let success = 0;
    for (const doc of docs) {
      try {
        const resp = await fetch(`${apiBase}/${collectionToApiPath(collection)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(doc),
        });
        if (resp.ok) {
          success++;
          appendToCache(collection, doc);
        } else {
          console.error(`[bulkAddDocs] ${collection} POST 失败 ${resp.status}:`, (await resp.text()).substring(0, 100));
        }
      } catch (e) {
        console.error(`[bulkAddDocs] ${collection} POST 异常:`, (e as Error).message);
      }
    }
    return { count: success };
  }

  // 非 REST 集合：保留本地写入
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  if (overwrite) {
    await db.run(`DELETE FROM ${collection}`);
  }

  for (const doc of docs) {
    const cols = Object.keys(doc).join(', ');
    const placeholders = Object.keys(doc).map(() => '?').join(', ');
    const values = Object.values(doc).map(v => String(v ?? ''));
    await db.run(`INSERT INTO ${collection} (${cols}) VALUES (${placeholders})`, values);

    // P2-O3: 批量 CREATE 审计（逐条记录）
    await tryWriteAuditForCRUD('create', collection, String(doc._id), doc);
  }

  await saveDB();
  clearCache(collection);

  return { count: docs.length };
}

// Update a document
export async function updateDoc(collection: string, id: string, data: Record<string, unknown>) {
  const now = new Date().toISOString();
  const updateData: Record<string, unknown> = { ...data, updatedAt: now };
  // 数据隔离：保证 merchant_id 不被清除
  if (updateData.merchant_id === undefined || updateData.merchant_id === null || updateData.merchant_id === '') {
    const globalMid = getCurrentUserMerchantId();
    if (globalMid) updateData.merchant_id = globalMid;
  }

  // 快递派件短信判断（需要旧状态）
  let shouldSendDeliverySMS = false;
  let expressDoc: Record<string, unknown> | null = null;
  if (collection === COL.EXPRESS && data.status === '派件中') {
    const currentDoc = await getDoc(collection, id);
    if (currentDoc && currentDoc.status !== '派件中') {
      shouldSendDeliverySMS = true;
      expressDoc = { _id: id, ...currentDoc, ...updateData };
    }
  }

  // ===== 核心改造：REST 集合直接 PUT 服务器，不再写本地 sql.js =====
  if (isRestCollection(collection)) {
    const token = await getRemoteToken().catch(() => '');
    const apiBase = getRemoteApiUrl();
    if (!token || !apiBase) {
      throw new Error('无可用登录令牌，请先登录');
    }
    const resp = await fetch(`${apiBase}/${collectionToApiPath(collection)}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updateData),
    });
    if (!resp.ok) {
      const errText = (await resp.text()).substring(0, 200);
      throw new Error(`服务器更新失败 ${resp.status}: ${errText}`);
    }

    // 只更新内存缓存中该记录，不清空整个 collection
    updateCacheById(collection, id, updateData);

    if (shouldSendDeliverySMS && expressDoc) {
      sendExpressSMS(expressDoc, 'delivery').catch(e => console.error('[updateDoc] 派件短信发送失败:', e));
    }
    return { updated: 1 };
  }

  // 非 REST 集合：保留本地写入
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  // P2-O3: EDIT 审计 - 更新前先获取旧快照
  let oldSnapshot: Record<string, unknown> | null = null;
  if (AUDIT_ACTION_MAP[collection]?.edit) {
    const results = await db.exec(`SELECT * FROM ${collection} WHERE _id = ?`, [id]);
    if (results.length > 0 && results[0].values.length > 0) {
      const cols = results[0].columns;
      const row = results[0].values[0];
      oldSnapshot = {};
      cols.forEach((c, i) => { (oldSnapshot as Record<string, unknown>)[c] = row[i]; });
    }
  }

  const sets = Object.keys(updateData).map(k => `${k} = ?`).join(', ');
  const values = [...Object.values(updateData).map(v => String(v ?? '')), id];
  await db.run(`UPDATE ${collection} SET ${sets} WHERE _id = ?`, values);

  if (oldSnapshot) {
    await tryWriteAuditForCRUD('edit', collection, id, oldSnapshot);
  }

  await saveDB();
  clearCache(collection);

  if (shouldSendDeliverySMS && expressDoc) {
    sendExpressSMS(expressDoc, 'delivery').catch(e => console.error('[updateDoc] 派件短信发送失败:', e));
  }

  return { updated: 1 };
}

// Delete a document
export async function deleteDoc(collection: string, id: string) {
  // ===== 核心改造：REST 集合直接 DELETE 服务器，不再删本地 sql.js =====
  if (isRestCollection(collection)) {
    const token = await getRemoteToken().catch(() => '');
    const apiBase = getRemoteApiUrl();
    if (!token || !apiBase) {
      throw new Error('无可用登录令牌，请先登录');
    }
    const resp = await fetch(`${apiBase}/${collectionToApiPath(collection)}/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}`, ...getAppBuildHeaders() },
    });
    if (!resp.ok) {
      const errText = (await resp.text()).substring(0, 200);
      throw new Error(`服务器删除失败 ${resp.status}: ${errText}`);
    }

    // 只从内存缓存中移除该记录
    removeFromCache(collection, id);

    return { deleted: 1 };
  }

  // 非 REST 集合：保留本地删除
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  // P2-O3: DELETE 审计 - 删除前先取旧快照
  let oldSnapshot: Record<string, unknown> | null = null;
  if (AUDIT_ACTION_MAP[collection]?.delete) {
    const results = await db.exec(`SELECT * FROM ${collection} WHERE _id = ?`, [id]);
    if (results.length > 0 && results[0].values.length > 0) {
      const cols = results[0].columns;
      const row = results[0].values[0];
      oldSnapshot = {};
      cols.forEach((c, i) => { (oldSnapshot as Record<string, unknown>)[c] = row[i]; });
    }
  }

  await db.run(`DELETE FROM ${collection} WHERE _id = ?`, [id]);

  if (oldSnapshot) {
    await tryWriteAuditForCRUD('delete', collection, id, oldSnapshot);
  }

  await saveDB();
  clearCache(collection);

  return { deleted: 1 };
}

// Get single document
export async function getDoc<T>(collection: string, id: string): Promise<T | null> {
  // ===== 核心改造：REST 集合直接 GET 服务器 =====
  if (isRestCollection(collection)) {
    const token = await getRemoteToken().catch(() => '');
    const apiBase = getRemoteApiUrl();
    if (token && apiBase) {
      try {
        const resp = await fetch(`${apiBase}/${collectionToApiPath(collection)}/${id}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json', ...getAppBuildHeaders() }
        });
        if (resp.ok) {
          const result = await resp.json().catch(() => null);
          return result as T;
        }
      } catch (e) {
        console.warn(`[getDoc] ${collection}/${id} API 异常，降级读本地:`, (e as Error).message);
      }
    }
  }

  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const results = await db.exec(`SELECT * FROM ${collection} WHERE _id = ?`, [id]);
  if (results.length === 0 || results[0].values.length === 0) return null;
  const columns = results[0].columns;
  const row = results[0].values[0];
  const obj: any = {};
  columns.forEach((col, i) => { obj[col] = row[i]; });
  return obj as T;
}

// Get setting
export async function getSetting(key: string): Promise<string> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const results = await db.exec(`SELECT * FROM settings WHERE key = ?`, [key]);
  if (results.length === 0 || results[0].values.length === 0) return '';
  const columns = results[0].columns;
  const row = results[0].values[0];
  const obj: any = {};
  columns.forEach((col, i) => { obj[col] = row[i]; });
  return obj.value || '';
}

// Set setting
export async function setSetting(key: string, value: string): Promise<{ success: boolean }> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  // Check if setting exists
  const existing = await getSetting(key);
  const now = new Date().toISOString();
  
  try {
    if (existing) {
      // Update existing - sql.js run() is synchronous
      await db.run(`UPDATE settings SET value = ?, updatedAt = ? WHERE key = ?`, [value, now, key]);
    } else {
      // Insert new - sql.js run() is synchronous
      const id = generateId();
      await db.run(`INSERT INTO settings (_id, key, value, createdAt, updatedAt) VALUES (?, ?, ?, ?, ?)`, [id, key, value, now, now]);
    }

    // P2-O3: 系统设置修改审计 - setting_modify_<key>
    if (_auditOperatorId || _auditOperatorName) {
      try {
        const snapshot = JSON.stringify({ key, oldValue: existing || '', newValue: value });
        const logId = generateId();
        await db.run(
          `INSERT INTO audit_logs (_id, action, operator_id, operator_name, target_id, target_snapshot, reason, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [logId, `setting_modify_${key}`, _auditOperatorId, _auditOperatorName, key, snapshot, '无', now]
        );
      } catch (e) {
        console.warn('[审计] 设置修改审计写入失败（忽略）:', e);
      }
    }
    
    await saveDB();
    clearCache(COL.SETTINGS);
    return { success: true };
  } catch (err) {
    throw new Error('数据库操作失败: ' + (err as Error).message);
  }
}

// Send WeCom webhook (通过主进程发送，避免 CORS 问题)
export async function sendWeComWebhook(webhookUrl: string, content: string): Promise<void> {
  if (!webhookUrl) return;
  console.log('[sendWeComWebhook] 发送企微消息, webhook:', webhookUrl);
  console.log('[sendWeComWebhook] 内容:', content);
  
  const result = await runtime.sendWecomWebhook(webhookUrl, content);
  console.log('[sendWeComWebhook] 返回:', result);
  
  if (!result.success) {
    throw new Error(result.error || '发送失败');
  }
}

// Send WeCom image webhook (通过主进程发送，避免 CORS 问题)
export async function sendWeComImageWebhook(webhookUrl: string, base64: string): Promise<void> {
  if (!webhookUrl) return;
  console.log('[sendWeComImageWebhook] 发送企微图片');
  
  const result = await runtime.sendWecomImageWebhook(webhookUrl, base64);
  console.log('[sendWeComImageWebhook] 返回:', result);
  
  if (!result.success) {
    throw new Error(result.error || '发送失败');
  }
}

// Format date
export function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}/${m}/${day}`;
}

export function todayStr(): string {
  return formatDate(new Date().toISOString());
}

// 获取所有本地数据（用于远程同步）
export async function getAllData(): Promise<Record<string, any[]>> {
  await initializeLocalDB();
  
  const collections = [
    COL.INFLUENCERS,
    COL.INFLUENCER_BLACKLIST,
    COL.PRODUCTS,
    COL.EXPRESS,
    COL.SAMPLES,
    COL.ORDERS,
    COL.ORDER_INFLUENCERS,
    COL.MERCHANTS
  ];
  
  const result: Record<string, any[]> = {};
  
  for (const collection of collections) {
    try {
      const data = await fetchAll(collection);
      result[collection] = data;
      console.log(`[getAllData] ${collection}: ${data.length} 条`);
    } catch (e) {
      console.error(`[getAllData] 获取 ${collection} 失败:`, e);
      result[collection] = [];
    }
  }
  
  return result;
}

// P0-UX-2: 统一商家端 API 地址（改回最简逻辑，别再整复杂了）
//
// 本 SaaS：后端就一套 users 表（MySQL）；商家端桌面端/Web 版都走 shop.dtuitui.com/api/login。
// 昨天桌面端登录失败 = localStorage 存了 dtuitui.com/localhost 这类 URL，POST 被 301 → body 丢
// 失 → 服务器表现成"账号密码错误"。前端别自作聪明搞一堆 localStorage 清留逻辑了。
//
// 最简规则：
//   - Electron 桌面端 / Web 版：
//       · 普通商家/员工登录 → https://shop.dtuitui.com/api（商家端后端 3002，按 merchant_id 隔离）
//       · 系统 admin 账号登录 → https://admin.dtuitui.com/api（管理端后端 3003，全局全量数据）
//     通过 localStorage 中的 admin_logged_in + admin_token 组合判断 admin 登录态；
//     登录成功后 LoginPage 会写这两个 key 并 reload 一次，让模块常量在这里重新计算。
function isAdminLoggedIn(): boolean {
  try {
    return typeof localStorage !== 'undefined'
      && localStorage.getItem('admin_logged_in') === '1'
      && !!String(localStorage.getItem('admin_token') || '').trim();
  } catch (_) { return false; }
}
function isDevMode(): boolean {
  try {
    if (typeof window !== 'undefined' && window.location) {
      const host = window.location.hostname;
      return host === 'localhost' || host === '127.0.0.1' || host === '';
    }
  } catch (_) {}
  return false;
}
function isWebRuntime(): boolean {
  try {
    return typeof window !== 'undefined'
      && typeof (window as any).__APP_RUNTIME_MODE__ === 'string'
      && (window as any).__APP_RUNTIME_MODE__ === 'web';
  } catch (_) { return false; }
}
function webOriginOr(fallback: string): string {
  // 开发模式：返回空字符串，调用方拼 `${''}/api` 形成相对路径走 Vite 代理
  if (isDevMode()) return '';
  try {
    if (typeof window !== 'undefined' && window.location?.origin && /^https?:/.test(window.location.protocol)) {
      return window.location.origin.replace(/\/+$/, '');
    }
  } catch (_) { /* ignore */ }
  return fallback;
}
export function getRemoteApiUrl(): string {
  if (isAdminLoggedIn()) {
    // admin 账号固定用管理端 API（拿全局商品/快递/样例/商家，不按 merchant_id 过滤）
    return 'https://admin.dtuitui.com/api';
  }
  // 开发模式：返回相对路径 /api，所有请求走 Vite 代理
  if (isDevMode()) return '/api';
  const base = isWebRuntime() ? webOriginOr('https://shop.dtuitui.com') : 'https://shop.dtuitui.com';
  return `${base}/api`;
}
// S4: 构建版本头（由 Vite define 注入；开发环境/未注入时 fallback 到 dev）
declare const __APP_BUILD_VERSION__: string | undefined;
declare const __APP_BUILD_TS__: string | undefined;
export const APP_BUILD_VERSION: string = (typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : '0.0.0-dev');
export const APP_BUILD_TS: string = (typeof __APP_BUILD_TS__ !== 'undefined' ? __APP_BUILD_TS__ : '00000000000000');
export function getAppBuildHeaders(): Record<string, string> {
  return {
    'X-App-Build-Version': APP_BUILD_VERSION,
    'X-App-Build-TS': APP_BUILD_TS,
    'X-App-Side': 'shop',
    'Cache-Control': 'no-store',
  };
}

// REMOTE_API_URL：提供给需要模块加载时立即求值的场景（LoginPage 初始加载公告等不会命中 admin 登录态，此处兜底即可）；
// 登录/同步等长生命周期场景请优先调 getRemoteApiUrl() 动态取值。
export const REMOTE_API_URL = getRemoteApiUrl();
export const LOCAL_API_URL = REMOTE_API_URL;   // 商家端统一用一套后端，别搞两路

// FIXED: 样品进度暂时不支持远程同步（远程服务器未创建表）
const SYNC_COLLECTIONS = [COL.INFLUENCERS, COL.INFLUENCER_BLACKLIST, COL.PRODUCTS, COL.EXPRESS, COL.SAMPLES, COL.ORDERS, COL.ORDER_INFLUENCERS, COL.MERCHANTS];

// 修复问题2+4: 只打印一次的降级日志标记（避免每次API调用都刷屏）
let _tokenFallbackLogged = false;

export async function getRemoteToken(): Promise<string> {
  // ===== FIXED: 同步专用 token 与业务登录 token 分离存储，避免互相覆盖导致 403 =====
  // 商家用户自己登录（POST /api/login 得到的 merchant/staff token）存在：remote_token
  // 管理端数据同步账号（admin 或低权限同步账号）存在：sync_token
  // 优先级：1) 同步账号 token → 2) 兜底用商家用户登录 token（merchant 身份也能同步自己商家的数据）
  const SYNC_KEY = 'sync_token';
  const SYNC_EXP_KEY = 'sync_token_expiry';
  const BUSINESS_KEY = 'remote_token';
  const BUSINESS_EXP_KEY = 'remote_token_expiry';

  // --- 方案1：尝试同步账号专用 token ---
  const syncCached = localStorage.getItem(SYNC_KEY);
  const syncExp = localStorage.getItem(SYNC_EXP_KEY);
  if (syncCached && syncExp && Date.now() < Number(syncExp)) {
    return syncCached;
  }

  try {
    // 读取管理端同步账号（settings.json → localStorage 兜底）
    let username = '';
    let password = '';
    try {
      username = await runtime.getSetting('remote_username');
      password = await runtime.getSetting('remote_password');
    } catch (_) { /* 静默：setting API 返回 null 或 404 是正常的降级情况 */ }
    if (!username || !password) {
      username = localStorage.getItem('remote_username') || '';
      password = localStorage.getItem('remote_password') || '';
    }

    if (username && password) {
      const response = await fetch(`${REMOTE_API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (response.ok) {
        const result = await response.json();
        const newToken = result.token;
        if (newToken) {
          // ✅ 写入独立的 sync_token，不再覆盖 remote_token（业务登录 token）
          localStorage.setItem(SYNC_KEY, newToken);
          localStorage.setItem(SYNC_EXP_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
          // 同步账号登录成功 → 重置降级标记
          _tokenFallbackLogged = false;
          return newToken;
        }
      } else {
        // 修复问题2: await 括号位置修正 + 只打一次日志
        if (!_tokenFallbackLogged) {
          let errText = '';
          try { errText = (await response.text()).substring(0, 200); } catch (_) { errText = String(response.status); }
          console.info('[getRemoteToken] 同步账号登录失败，使用商家 token 兜底（仅首次提示）:', errText);
          _tokenFallbackLogged = true;
        }
        // 去掉 showToast — 每次 API 调用都弹会干扰用户
      }
    }
  } catch (_) { /* 静默：同步账号不可用是常态 */ }

  // --- 方案2 兜底：用商家用户自己登录的 token（merchant/staff）同步本商家数据 ---
  const businessToken = localStorage.getItem(BUSINESS_KEY);
  const businessExp = localStorage.getItem(BUSINESS_EXP_KEY);
  if (businessToken && (!businessExp || Date.now() < Number(businessExp))) {
    return businessToken;
  }
  throw new Error('无可用远程同步令牌：请先登录商家账号，或在「设置→远程同步」配置有效的管理端同步账号');
}

// FIXED: 仅本地使用的字段（远程服务器不存在这些列）
// 2026-08-17 补全：今天出现了 remoteId 不存在、passwordHash 发过去也可能被拒的问题，
// 故将所有本地计算/本地 SQLite 专用的字段统一在此处集中声明，同步 cleanDocForSync 过滤时全部丢掉。
const LOCAL_ONLY_FIELDS = [
  'createdBy',      // 老版本遗留：本地创建者标记
  'smsSent',        // 本地短信发送状态
  'passwordHash',   // 本地 SQLite 存的 bcrypt 哈希（远程 users.employees 表有独立的 password 字段，不能混）
  'remoteId',       // 本地记录关联的远程 DB 主键（SQLite 表结构中不存在该列，之前写入时直接 ER_BAD_FIELD_ERROR）
  'deleted',        // 本地软删除标记（远程统一走 deleted=1，不通过同步传递，避免误删）
  'archived',       // 本地归档标记（同 deleted）
  'archivedAt',     // 本地归档时间
  'localOnly',      // 开发者手动标记的本地-only 字段
  'synced',         // 同步状态辅助字段（本地内存用，远程不认）
  'dirty'           // 脏数据标记（同上）
];

// FIXED: 添加数据清理函数，防止空字符串导致服务器校验错误
function cleanDocForSync(doc: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(doc)) {
    // FIXED: 过滤掉仅本地使用的字段（远程服务器不存在这些列）
    if (LOCAL_ONLY_FIELDS.includes(key)) {
      continue;
    }
    // 保留 _id, createdAt, updatedAt 等系统字段
    if (key.startsWith('_') || key === 'createdAt' || key === 'updatedAt') {
      cleaned[key] = value;
      continue;
    }
    // 空字符串转为 null，避免服务器端 "Zero-length string" 错误
    if (typeof value === 'string' && value.trim() === '') {
      cleaned[key] = null;
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

async function syncToRemote(collection: string, doc: Record<string, unknown>): Promise<void> {
  if (!SYNC_COLLECTIONS.includes(collection)) {
    return;
  }

  // ===== FIXED: 商家归属过滤 - 杜绝跨商家同步导致的 403 / Duplicate entry =====
  // 1) 获取当前登录身份所属的 merchant_id
  const currentMid = getCurrentUserMerchantId();
  // 2) 记录上如果已显式带 merchant_id，且与当前身份不匹配 → 直接跳过（残留的其他商家脏数据）
  const docMid = doc.merchant_id ? String(doc.merchant_id) : '';
  if (docMid && currentMid && docMid !== currentMid) {
    console.warn(`[syncToRemote] 跳过非本商家记录（归属冲突）: ${collection}#${String(doc._id || '').slice(0, 8)} doc_mid=${docMid} current_mid=${currentMid}`);
    return;
  }
  
  try {
    const token = await getRemoteToken();
    const id = doc._id;
    const endpoint = `/${collectionToApiPath(collection)}`;
    // FIXED: 清理数据，将空字符串转为 null
    const cleanedDoc = cleanDocForSync(doc) as Record<string, unknown>;
    // 数据隔离：同步前保证 merchant_id（商家端只同步自己的）
    if (!cleanedDoc.merchant_id) {
      const mid = currentMid || getCurrentUserMerchantId();
      if (mid) (cleanedDoc as any).merchant_id = mid;
    }
    // 再次保护：清理后若归属仍冲突 → 跳过
    const afterMid = (cleanedDoc.merchant_id as string) ? String(cleanedDoc.merchant_id) : '';
    if (afterMid && currentMid && afterMid !== currentMid) {
      console.warn(`[syncToRemote] 清理后归属仍冲突，跳过: ${collection}#${String(id || '').slice(0, 8)}`);
      return;
    }
    
    if (id) {
      const existing = await fetch(`${REMOTE_API_URL}${endpoint}/${id}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getAppBuildHeaders() }
      });
      
      if (existing.ok) {
        const response = await fetch(`${REMOTE_API_URL}${endpoint}/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(cleanedDoc)
        });
        // FIXED: 检查响应状态，处理服务器端校验错误
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[syncToRemote] ${collection} PUT失败 ${response.status}:`, errorText);
          return;
        }
      } else if (existing.status === 403) {
        // FIXED: 记录存在但属于其他商家 → 跳过，避免 POST 时主键 Duplicate entry
        console.warn(`[syncToRemote] ${collection} 记录归属其他商家(${currentMid})，跳过: ${String(id).slice(0, 8)}`);
        return;
      } else {
        const response = await fetch(`${REMOTE_API_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(cleanedDoc)
        });
        // FIXED: 检查响应状态
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[syncToRemote] ${collection} POST失败 ${response.status}:`, errorText);
          return;
        }
      }
    } else {
      const response = await fetch(`${REMOTE_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cleanedDoc)
      });
      // FIXED: 检查响应状态
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[syncToRemote] ${collection} POST失败 ${response.status}:`, errorText);
        return;
      }
    }
    
    console.log(`[syncToRemote] ${collection} 数据同步成功:`, id);
  } catch (e) {
    console.error(`[syncToRemote] ${collection} 同步失败:`, e);
  }
}

async function deleteFromRemote(collection: string, id: string): Promise<void> {
  if (!SYNC_COLLECTIONS.includes(collection)) {
    return;
  }

  // ===== FIXED: 删除前同样做归属检查，避免误删其他商家数据 =====
  const currentMid = getCurrentUserMerchantId();
  if (currentMid && db) {
    try {
      const r = await db.exec(`SELECT merchant_id FROM ${collection} WHERE _id = ?`, [id]);
      if (r.length > 0 && r[0].values.length > 0) {
        const docMid = r[0].values[0][0] ? String(r[0].values[0][0]) : '';
        if (docMid && docMid !== currentMid) {
          console.warn(`[deleteFromRemote] 跳过非本商家记录: ${collection}#${String(id).slice(0, 8)} doc_mid=${docMid} current_mid=${currentMid}`);
          return;
        }
      }
    } catch (_) { /* 本地查不到就放行，交给服务器端鉴权 */ }
  }
  
  try {
    const token = await getRemoteToken();
    const endpoint = `/${collectionToApiPath(collection)}`;
    
    const response = await fetch(`${REMOTE_API_URL}${endpoint}/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}`, ...getAppBuildHeaders() }
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[deleteFromRemote] ${collection} DELETE失败 ${response.status}:`, errorText);
      return;
    }
    
    console.log(`[deleteFromRemote] ${collection} 数据删除成功:`, id);
  } catch (e) {
    console.error(`[deleteFromRemote] ${collection} 删除失败:`, e);
  }
}

export async function syncAllDataToRemote(): Promise<{ success: number; failed: number }> {
  let success = 0;
  let failed = 0;
  
  for (const collection of SYNC_COLLECTIONS) {
    try {
      const dataList = await fetchAll<any>(collection);
      
      for (const item of dataList) {
        try {
          await syncToRemote(collection, item);
          success++;
        } catch (e) {
          console.error(`[syncAllDataToRemote] ${collection} 同步失败 _id=${item._id}:`, e);
          failed++;
        }
      }
    } catch (e) {
      console.error(`[syncAllDataToRemote] 获取本地 ${collection} 数据失败:`, e);
    }
  }
  
  return { success, failed };
}

export async function startAutoSync() {
  // FIXED: 每次启动都同步，不再限制24小时
  try {
    const autoSyncEnabled = await getSetting('auto_sync');
    if (autoSyncEnabled !== 'true') {
      if (!runtime.isWeb) console.log('[startAutoSync] 自动同步未开启，跳过');
      return;
    }
  } catch (e) {
    console.log('[startAutoSync] 获取自动同步设置失败:', e);
    return;
  }
  
  console.log('[startAutoSync] 客户端启动，开始自动同步所有数据到阿里云...');
  try {
    const result = await syncAllDataToRemote();
    console.log(`[startAutoSync] 自动同步完成: 成功 ${result.success} 条，失败 ${result.failed} 条`);
  } catch (e) {
    console.error('[startAutoSync] 自动同步失败:', e);
  }
}

// FIXED: 商家信息上报 - upsert 到本地 merchants 表 + 同步到远程
// 管理端通过拉取 merchants 集合获取所有商家列表
export async function upsertMerchantReport(report: Record<string, unknown>): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const id = String(report._id);
  // 本地 upsert
  const existing = await db.exec(`SELECT _id FROM merchants WHERE _id = ?`, [id]);
  if (existing.length > 0 && existing[0].values.length > 0) {
    const fields = Object.keys(report).filter(k => k !== '_id');
    const sets = fields.map(f => `${f} = ?`).join(', ');
    const vals = fields.map(f => report[f]);
    await db.run(`UPDATE merchants SET ${sets} WHERE _id = ?`, [...vals, id]);
  } else {
    const cols = Object.keys(report);
    const placeholders = cols.map(() => '?').join(', ');
    const vals = cols.map(c => report[c]);
    await db.run(`INSERT INTO merchants (${cols.join(', ')}) VALUES (${placeholders})`, vals);
  }
  await saveDB();
  clearCache(COL.MERCHANTS);
  // 同步到远程（复用 syncToRemote，merchants 已加入 SYNC_COLLECTIONS）
  try {
    await syncToRemote(COL.MERCHANTS, report);
  } catch (e) {
    console.error('[upsertMerchantReport] 同步远程失败:', e);
  }
}

// FIXED: 样品进度自动生成 - 当快递状态变为已签收/已代签收时调用
// 通过 expressId 去重：同一快递只生成一条进度记录
// 修复软删除：如果记录已被软删除(deleted=1)，不重新创建
export async function upsertSampleProgress(data: Record<string, unknown>): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const expressId = String(data.expressId);
  const now = new Date().toISOString();

  // 查找是否已存在该快递的进度记录（包括已软删除的）
  const existing = await db.exec(`SELECT _id, deleted FROM sample_progress WHERE expressId = ?`, [expressId]);
  if (existing.length > 0 && existing[0].values.length > 0) {
    const row = existing[0].values[0];
    const existingId = String(row[0]);
    const isDeleted = row[1] === 1 || row[1] === '1';
    
    if (isDeleted) {
      // FIXED: 如果记录已被软删除，跳过同步（不重新创建）
      return;
    }
    
    // 已存在且未删除 → 只更新签收相关字段
    const updateFields: Record<string, unknown> = {};
    for (const key of ['signTime', 'accountName', 'sampleName', 'expressNo']) {
      if (data[key] !== undefined && data[key] !== null) {
        updateFields[key] = data[key];
      }
    }
    updateFields.updatedAt = now;
    const sets = Object.keys(updateFields).map(f => `${f} = ?`).join(', ');
    const vals = Object.values(updateFields).map(v => String(v));
    vals.push(existingId);
    await db.run(`UPDATE sample_progress SET ${sets} WHERE _id = ?`, vals);
    await saveDB();
    clearCache(COL.SAMPLE_PROGRESS);
  } else {
    // 不存在 → 插入新记录
    const id = generateId();
    const doc = {
      _id: id,
      expressId: data.expressId,
      expressNo: data.expressNo || '',
      signTime: data.signTime || '',
      accountName: data.accountName || '',
      sampleName: data.sampleName || '',
      followStatus: '',
      followTime: '',
      followLogs: '[]',
      createdBy: data.createdBy || '',
      createdAt: now,
      updatedAt: now,
      deleted: 0,
    };
    const cols = Object.keys(doc).join(', ');
    const placeholders = Object.keys(doc).map(() => '?').join(', ');
    const vals = Object.values(doc).map(v => String(v));
    await db.run(`INSERT INTO sample_progress (${cols}) VALUES (${placeholders})`, vals);
    await saveDB();
    clearCache(COL.SAMPLE_PROGRESS);
  }
}

// FIXED: 更新样品进度的跟进信息（跟进进度+跟进时间+追加日志+附件）
// P1-B1: 新增 attachments 参数，存储附件文件路径（Method A：本地目录）
export async function updateSampleProgressFollow(id: string, followStatus: string, remark: string, attachments?: Array<{ name: string; path: string; size: number }>): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const now = new Date().toISOString();
  const todayStr = new Date().toISOString().split('T')[0];

  // 读取当前记录的 followLogs
  const existing = await db.exec(`SELECT followLogs FROM sample_progress WHERE _id = ?`, [id]);
  let logs: Array<{ time: string; status: string; remark: string; attachments?: Array<{ name: string; path: string; size: number }> }> = [];
  if (existing.length > 0 && existing[0].values.length > 0) {
    try {
      logs = JSON.parse(String(existing[0].values[0][0] || '[]'));
    } catch { logs = []; }
  }

  // 追加新的跟进记录（含备注和附件）
  const newLog: any = { time: now, status: followStatus, remark: remark || '' };
  if (attachments && attachments.length > 0) {
    newLog.attachments = attachments;
  }
  logs.push(newLog);

  await db.run(
    `UPDATE sample_progress SET followStatus = ?, followTime = ?, followLogs = ?, updatedAt = ? WHERE _id = ?`,
    [followStatus, todayStr, JSON.stringify(logs), now, id]
  );
  await saveDB();
  clearCache(COL.SAMPLE_PROGRESS);

  // 同步到远程
  try {
    const doc = await getDoc('sample_progress', id);
    if (doc) await syncToRemote(COL.SAMPLE_PROGRESS, doc);
  } catch (e) {
    console.error('[updateSampleProgressFollow] 同步远程失败:', e);
  }
}

// FIXED: 软删除样品进度记录 - 标记 deleted=1，防止同步时重新创建
// P0-S2: 删除前自动写入审计日志（含完整数据快照 + 删除原因）
export async function softDeleteSampleProgress(id: string, operatorId: string, operatorName: string, reason: string): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const now = new Date().toISOString();

  // 读取被删除记录的完整快照
  const snapResult = await db.exec(`SELECT * FROM sample_progress WHERE _id = ?`, [id]);
  let snapshot = '';
  if (snapResult.length > 0 && snapResult[0].values.length > 0) {
    const cols = snapResult[0].columns;
    const row = snapResult[0].values[0];
    const obj: Record<string, any> = {};
    cols.forEach((col, i) => { obj[col] = row[i]; });
    snapshot = JSON.stringify(obj);
  }

  // 写入审计日志（只可追加）
  const logId = generateId();
  await db.run(
    `INSERT INTO audit_logs (_id, action, operator_id, operator_name, target_id, target_snapshot, reason, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [logId, 'sample_progress_delete', operatorId, operatorName, id, snapshot, reason || '无', now]
  );

  // 执行软删除
  await db.run(`UPDATE sample_progress SET deleted = 1, updatedAt = ? WHERE _id = ?`, [now, id]);
  await saveDB();
  clearCache(COL.SAMPLE_PROGRESS);
}

// P0-S2: 写入审计日志（通用函数，后续模块复用）
export async function addAuditLog(action: string, operatorId: string, operatorName: string, targetId: string, snapshot: string, reason: string = ''): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const logId = generateId();
  const now = new Date().toISOString();
  await db.run(
    `INSERT INTO audit_logs (_id, action, operator_id, operator_name, target_id, target_snapshot, reason, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [logId, action, operatorId, operatorName, targetId, snapshot, reason || '无', now]
  );
  await saveDB();
}

// P0-S2: 查询审计日志
export async function fetchAuditLogs(filters?: { action?: string; operatorId?: string; startDate?: string; endDate?: string }): Promise<any[]> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  let sql = `SELECT * FROM audit_logs`;
  const conditions: string[] = [];
  const params: any[] = [];
  if (filters?.action) { conditions.push('action = ?'); params.push(filters.action); }
  if (filters?.operatorId) { conditions.push('operator_id = ?'); params.push(filters.operatorId); }
  if (filters?.startDate) { conditions.push('created_at >= ?'); params.push(filters.startDate); }
  if (filters?.endDate) { conditions.push('created_at <= ?'); params.push(filters.endDate); }
  if (conditions.length > 0) sql += ' WHERE ' + conditions.join(' AND ');
  sql += ' ORDER BY created_at DESC';
  const result = await db.exec(sql, params);
  if (result.length === 0) return [];
  const columns = result[0].columns;
  return result[0].values.map(row => {
    const doc: Record<string, any> = {};
    columns.forEach((col, i) => { doc[col] = row[i]; });
    return doc;
  });
}

// FIXED: 获取未删除的样品进度记录
export async function fetchActiveSampleProgress(): Promise<any[]> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const result = await db.exec(`SELECT * FROM sample_progress WHERE deleted = 0 OR deleted IS NULL`);
  if (result.length === 0) return [];
  const columns = result[0].columns;
  return result[0].values.map(row => {
    const doc: Record<string, any> = {};
    columns.forEach((col, i) => {
      doc[col] = row[i];
    });
    return doc;
  });
}

export async function syncSettingsToRemote(): Promise<void> {
  try {
    const autoSyncEnabled = await getSetting('auto_sync');
    if (autoSyncEnabled !== 'true') {
      return;
    }
    
    const token = await getRemoteToken();
    const settings = await fetchAll(COL.SETTINGS);
    
    await fetch(`${REMOTE_API_URL}/sync/all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ data: { settings } })
    });
    
    console.log('[syncSettingsToRemote] 设置同步成功');
  } catch (e) {
    console.error('[syncSettingsToRemote] 设置同步失败:', e);
  }
}

export async function sendSMS(phone: string, templateParam: Record<string, unknown>, templateType: 'create' | 'delivery'): Promise<boolean> {
  try {
    const signName = await runtime.getSetting('sms_sign_name');
    const templateCodeKey = templateType === 'create' ? 'sms_create_template_code' : 'sms_delivery_template_code';
    const templateCode = await runtime.getSetting(templateCodeKey);
    
    console.log(`[sendSMS] 获取配置: signName=${signName}, templateCode=${templateCode}, templateType=${templateType}`);
    
    if (!signName || !templateCode) {
      console.log(`[sendSMS] ${templateType === 'create' ? '发货通知' : '派件通知'}模板未配置，跳过发送`);
      return false;
    }
    
    const token = await getRemoteToken();
    console.log(`[sendSMS] token获取成功`);
    
    const response = await fetch(`${REMOTE_API_URL}/sms/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        phone,
        signName,
        templateCode,
        templateParam
      })
    });
    
    const result = await response.json();
    console.log(`[sendSMS] 响应结果:`, result);
    
    if (result.success) {
      console.log(`[sendSMS] ${templateType === 'create' ? '发货通知' : '派件通知'}短信发送成功: ${phone}`);
      // P1-B5: 记录短信扣费明细（单价 0.045 元/条，阿里云短信标准价）
      addFeeRecord('sms', phone, 0.045, 1, '系统', undefined).catch(() => {});
      // 扣减管理端共享短信库存（失败不阻断主流程）
      consumeStock('sms', 1, phone).catch(e => console.warn('[sendSMS] 共享库存扣减失败:', e));
      return true;
    } else {
      console.error(`[sendSMS] ${templateType === 'create' ? '发货通知' : '派件通知'}短信发送失败: ${phone}, error: ${result.error}, code: ${result.code}`);
      return false;
    }
  } catch (e) {
    console.error(`[sendSMS] ${templateType === 'create' ? '发货通知' : '派件通知'}短信发送异常: ${phone}, error:`, e);
    return false;
  }
}

export async function addSMSRecord(info: {
  smsType: '取件' | '单号' | '签收' | '流量券' | '测试' | string;
  templateKey?: string;
  phone: string;
  influencerName?: string;
  status: 'success' | 'failed';
  errorMsg?: string;
  expressNo?: string;
  remark?: string;
  bizId?: string;
}): Promise<void> {
  await ensureDBInitialized();
  if (!db) return;
  const { id, name } = getAuditOperator();
  const now = new Date().toISOString();
  const cols = ['_id', 'sent_at', 'operator_name', 'operator_id', 'influencer_name', 'recipient_phone',
    'sms_type', 'template_key', 'status', 'error_msg', 'express_no', 'remark', 'merchant_id', 'biz_id'];
  const vals = [
    generateId(), now, name || '系统', id || '', info.influencerName || '', info.phone,
    info.smsType, info.templateKey || '', info.status, info.errorMsg || '', info.expressNo || '',
    info.remark || '', getCurrentUserMerchantId() || '', info.bizId || '',
  ];
  const ph = cols.map(() => '?').join(', ');
  await db.run(`INSERT INTO sms_records (${cols.join(', ')}) VALUES (${ph})`, vals);
}

/** 查询最近的短信发送记录，默认最近 50 条倒序 */
export async function listSMSRecords(limit = 50): Promise<any[]> {
  await ensureDBInitialized();
  if (!db) return [];
  const rows = await db.all(
    `SELECT * FROM sms_records ORDER BY sent_at DESC LIMIT ?`,
    [limit]
  );
  return rows as any[];
}

export async function sendExpressSMS(expressData: Record<string, unknown>, type: 'create' | 'delivery'): Promise<void> {
  console.log('[sendExpressSMS] 开始发送短信, type=', type, ', data=', expressData);

  const phone = expressData.phone as string;
  if (!phone) {
    console.log('[sendExpressSMS] 无手机号，跳过发送');
    return;
  }

  console.log('[sendExpressSMS] 手机号:', phone);

  const templateParam: Record<string, unknown> = {
    expressNo: expressData.expressNo || '',
    sampleName: expressData.sampleName || '',
    shortName: expressData.shortName || '',
    recipientName: expressData.recipientName || '',
    platform: expressData.platform || '',
    expressCompany: expressData.expressCompany || '',
    status: type === 'delivery' ? '派件中' : '已发货'
  };

  console.log('[sendExpressSMS] 模板参数:', templateParam);

  const ok = await sendSMS(phone, templateParam, type);
  // 记录短信发送记录
  const smsType = type === 'create' ? '取件' : '单号';
  addSMSRecord({
    smsType,
    templateKey: type,
    phone,
    influencerName: String(expressData.recipientName || ''),
    status: ok ? 'success' : 'failed',
    expressNo: String(expressData.expressNo || ''),
    errorMsg: ok ? '' : '调用失败或模板未配置',
  }).catch(() => {});
}

// P1-B5: 记录资费消耗明细
export async function addFeeRecord(feeType: string, refNo: string, unitPrice: number, count: number, operatorName: string, balanceAfter?: number): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const id = generateId();
  const now = new Date().toISOString();
  const total = (unitPrice * count).toFixed(4);
  await db.run(
    `INSERT INTO fee_ledger (_id, fee_type, ref_no, unit_price, count, total_amount, operator_name, balance_after, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [id, feeType, refNo, String(unitPrice), String(count), total, operatorName, balanceAfter != null ? String(balanceAfter) : '', now]
  );
  await saveDB();
}

// P1-B5: 查询资费消耗明细
export async function fetchFeeRecords(startDate?: string, endDate?: string): Promise<any[]> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  let sql = `SELECT * FROM fee_ledger`;
  const conditions: string[] = [];
  const params: any[] = [];
  if (startDate) { conditions.push('created_at >= ?'); params.push(startDate); }
  if (endDate) { conditions.push('created_at <= ?'); params.push(endDate); }
  if (conditions.length > 0) sql += ' WHERE ' + conditions.join(' AND ');
  sql += ' ORDER BY created_at DESC';
  const result = await db.exec(sql, params);
  if (result.length === 0) return [];
  const columns = result[0].columns;
  return result[0].values.map(row => {
    const doc: Record<string, any> = {};
    columns.forEach((col, i) => { doc[col] = row[i]; });
    return doc;
  });
}

// ========== 共享库存消耗（管理端全局库存池）==========
// type: 'sms' | 'express' | 'waybill'
// 返回 { success, remaining, error? }
export async function consumeStock(type: 'sms' | 'express' | 'waybill', amount: number, refNo?: string): Promise<{ success: boolean; remaining?: number; error?: string }> {
  try {
    const token = await getRemoteToken();
    const res = await fetch(`${REMOTE_API_URL}/merchant/stock-consume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...getAppBuildHeaders() },
      body: JSON.stringify({ type, amount, refNo, operatorName: '商家端' }),
    });
    if (res.status === 401) return { success: false, error: '登录已过期，请重新登录' };
    if (res.status === 404) return { success: false, error: '库存配置未初始化' };
    if (!res.ok) return { success: false, error: `服务器错误(${res.status})` };
    const data = await res.json();
    return { success: !!data.success, remaining: data.remaining, error: data.error };
  } catch (e: any) {
    console.warn('[consumeStock] 请求异常:', e.message);
    return { success: false, error: e.message };
  }
}

// ========== P2-O2: Webhook 分组管理 ==========
export interface WebhookGroup {
  _id?: string;
  group_name: string;
  group_desc?: string;
  webhook_urls?: string[]; // 存储为 JSON string: Array<string>
  created_at?: string;
  updated_at?: string;
}

// Webhook 分组实体 -> DB 行
function serializeGroup(g: WebhookGroup): Omit<WebhookGroup, 'webhook_urls'> & { webhook_urls?: string } {
  return { ...g, webhook_urls: g.webhook_urls ? JSON.stringify(g.webhook_urls) : '[]' };
}

// DB 行 -> Webhook 分组实体
function deserializeGroup(row: Record<string, any>): WebhookGroup {
  let urls: string[] = [];
  try {
    urls = row.webhook_urls ? JSON.parse(row.webhook_urls) : [];
  } catch { urls = []; }
  return { ...row, webhook_urls: urls };
}

// 查询所有分组
export async function fetchWebhookGroups(): Promise<WebhookGroup[]> {
  const rows = await fetchAll<Record<string, any>>(COL.WEBHOOK_GROUPS);
  return rows.map(deserializeGroup).sort((a, b) =>
    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  );
}

// 新增/更新分组
export async function upsertWebhookGroup(g: WebhookGroup): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  const now = new Date().toISOString();
  const id = g._id || generateId();
  const serialized = serializeGroup(g);

  const existing = await db.exec(`SELECT _id FROM ${COL.WEBHOOK_GROUPS} WHERE _id = ?`, [id]);
  if (existing.length > 0 && existing[0].values.length > 0) {
    await db.run(
      `UPDATE ${COL.WEBHOOK_GROUPS} SET group_name=?, group_desc=?, webhook_urls=?, updated_at=? WHERE _id=?`,
      [serialized.group_name || '', serialized.group_desc || '', serialized.webhook_urls || '[]', now, id]
    );
  } else {
    await db.run(
      `INSERT INTO ${COL.WEBHOOK_GROUPS} (_id, group_name, group_desc, webhook_urls, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)`,
      [id, serialized.group_name || '', serialized.group_desc || '', serialized.webhook_urls || '[]', now, now]
    );
  }
  await saveDB();
  clearCache(COL.WEBHOOK_GROUPS);
}

// 删除分组
export async function deleteWebhookGroup(id: string): Promise<void> {
  await ensureDBInitialized();
  if (!db) { /* 纯 REST 架构：本地 sql.js 已禁用，不抛错 */ }
  await db.run(`DELETE FROM ${COL.WEBHOOK_GROUPS} WHERE _id = ?`, [id]);
  await saveDB();
  clearCache(COL.WEBHOOK_GROUPS);
}

// 通过一组 groupIds 收集所有唯一 webhook URL
export async function collectGroupWebhookUrls(groupIds: string[]): Promise<string[]> {
  const all = await fetchWebhookGroups();
  const urlSet = new Set<string>();
  for (const g of all) {
    if (g._id && groupIds.includes(g._id) && g.webhook_urls) {
      g.webhook_urls.forEach(u => { if (u && u.trim()) urlSet.add(u.trim()); });
    }
  }
  return Array.from(urlSet);
}

// 分组批量发送企微文本消息（成功/失败汇总）
export async function sendWeComWebhookToGroups(
  groupIds: string[],
  content: string
): Promise<{ success: number; failed: number; failedUrls: string[] }> {
  const urls = await collectGroupWebhookUrls(groupIds);
  let success = 0;
  let failed = 0;
  const failedUrls: string[] = [];
  for (const url of urls) {
    try {
      await sendWeComWebhook(url, content);
      success++;
    } catch (e) {
      failed++;
      failedUrls.push(url);
      console.warn('[分组推送] 失败:', url, e);
    }
  }
  return { success, failed, failedUrls };
}

// ===== P2-O4: 员工月度发样额度管控 =====

/**
 * 获取当前月份的 YYYY-MM 字符串
 */
export function getCurrentMonthStr(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * 判断某个 recordDate（YYYY-MM-DD 或其他格式）是否属于指定月份（YYYY-MM）
 */
function isInMonth(dateStr: string | undefined | null, monthStr: string): boolean {
  if (!dateStr) return false;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}` === monthStr;
  } catch {
    return false;
  }
}

/**
 * 获取指定员工本月已发样数量
 * @param employeeName 员工姓名（对应 samples.createdBy）
 */
export async function getEmployeeMonthlySampleCount(
  employeeName: string,
  monthStr?: string
): Promise<number> {
  await ensureDBInitialized();
  const targetMonth = monthStr || getCurrentMonthStr();
  const samples = await fetchAll<any>(COL.SAMPLES, {}, true);
  return samples.filter(s => s.createdBy === employeeName && isInMonth(s.recordDate, targetMonth)).length;
}

/**
 * 获取指定员工的月度发样额度
 * @returns 额度数值，0 或 null 表示不限制
 */
export async function getEmployeeMonthlyQuota(employeeName: string): Promise<number> {
  await ensureDBInitialized();
  const employees = await fetchAll<any>(COL.EMPLOYEES, {}, true);
  const emp = employees.find(e => e.name === employeeName);
  if (!emp) return 0;
  const quota = Number(emp.monthly_sample_quota);
  return isNaN(quota) ? 0 : quota;
}

/**
 * 检查员工是否还能新增发样（额度校验）
 * @param employeeName 员工姓名
 * @param count 本次计划新增数量（默认1）
 * @returns { allowed: boolean; used: number; quota: number; remaining: number }
 *          allowed=true 表示可以发样；quota=0 表示不限制
 */
export async function checkSampleQuota(
  employeeName: string,
  count: number = 1
): Promise<{ allowed: boolean; used: number; quota: number; remaining: number }> {
  const [quota, used] = await Promise.all([
    getEmployeeMonthlyQuota(employeeName),
    getEmployeeMonthlySampleCount(employeeName)
  ]);

  // quota = 0 表示不限制
  if (quota <= 0) {
    return { allowed: true, used, quota: 0, remaining: Infinity as unknown as number };
  }

  const remaining = quota - used;
  return {
    allowed: remaining >= count,
    used,
    quota,
    remaining: Math.max(0, remaining)
  };
}

/**
 * 批量获取员工本月额度使用情况（用于列表展示）
 * @param employeeNames 员工姓名数组
 */
export async function getEmployeesQuotaUsage(
  employeeNames: string[]
): Promise<Record<string, { used: number; quota: number; remaining: number }>> {
  const result: Record<string, { used: number; quota: number; remaining: number }> = {};
  const monthStr = getCurrentMonthStr();
  const [allSamples, allEmployees] = await Promise.all([
    fetchAll<any>(COL.SAMPLES, {}, true),
    fetchAll<any>(COL.EMPLOYEES, {}, true)
  ]);

  for (const name of employeeNames) {
    const used = allSamples.filter(s => s.createdBy === name && isInMonth(s.recordDate, monthStr)).length;
    const emp = allEmployees.find(e => e.name === name);
    const quota = emp ? (Number(emp.monthly_sample_quota) || 0) : 0;
    const remaining = quota > 0 ? Math.max(0, quota - used) : (Infinity as unknown as number);
    result[name] = { used, quota, remaining };
  }

  return result;
}

// ================= 第三期：达人分层 + 预算管控 + 风险预警 =================

// 达人分层等级
export const INFLUENCER_TIERS = ['S', 'A', 'B', 'C', 'D'] as const;
export const TIER_LABELS: Record<string, string> = {
  S: 'S-头部', A: 'A-优质', B: 'B-潜力', C: 'C-普通', D: 'D-黑名单'
};
export const TIER_COLORS: Record<string, string> = {
  S: '#ff3b30', A: '#ff9500', B: '#34c759', C: '#8e8e93', D: '#000000'
};

/**
 * 校验D级达人月度发样频次
 */
export async function checkTierDSampleLimit(influencerName: string): Promise<{ allowed: boolean; used: number; limit: number }> {
  const monthStr = getCurrentMonthStr();
  const allSamples = await fetchAll<any>(COL.SAMPLES, {}, true);
  const used = allSamples.filter(s =>
    s.accountName === influencerName && isInMonth(s.recordDate, monthStr)
  ).length;
  const limit = 2; // D级达人每月最多2次发样
  return { allowed: used < limit, used, limit };
}

/**
 * 校验近30天同款样品重复领取
 */
export async function checkDuplicateSample(influencerName: string, sampleName: string): Promise<{ duplicated: boolean; lastDate?: string }> {
  const allSamples = await fetchAll<any>(COL.SAMPLES, {}, true);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const thirtyDaysStr = thirtyDaysAgo.toISOString().slice(0, 10);

  const dup = allSamples.find(s =>
    s.accountName === influencerName &&
    s.sampleName === sampleName &&
    s.recordDate && s.recordDate >= thirtyDaysStr
  );
  return { duplicated: !!dup, lastDate: dup?.recordDate };
}

/**
 * 获取工作组月度预算使用情况
 */
export async function getWorkgroupBudgetUsage(workgroupId: string): Promise<{
  freeQuotaLimit: number; freeQuotaUsed: number;
  freeCostLimit: number; freeCostUsed: number;
  paidQuotaLimit: number; paidQuotaUsed: number;
  paidCostLimit: number; paidCostUsed: number;
  freeQuotaPercent: number; paidQuotaPercent: number;
}> {
  const monthStr = getCurrentMonthStr();
  const [budgets, workgroups, allSamples] = await Promise.all([
    fetchAll<any>(COL.WORKGROUP_BUDGETS, {}, true),
    fetchAll<any>(COL.WORKGROUPS, {}, true),
    fetchAll<any>(COL.SAMPLES, {}, true)
  ]);

  let budget = budgets.find(b => b.workgroup_id === workgroupId && b.month === monthStr);
  const wg = workgroups.find(w => w._id === workgroupId);
  const freeQuotaLimit = Number(budget?.free_sample_limit || wg?.monthly_free_quota || 0);
  const freeCostLimit = Number(budget?.free_cost_limit || wg?.monthly_free_cost || 0);
  const paidQuotaLimit = Number(budget?.paid_sample_limit || wg?.monthly_paid_quota || 0);
  const paidCostLimit = Number(budget?.paid_cost_limit || wg?.monthly_paid_cost || 0);

  const monthSamples = allSamples.filter(s => s.recordDate && isInMonth(s.recordDate, monthStr) && s.workgroup_id === workgroupId);
  const freeQuotaUsed = monthSamples.length;
  const freeCostUsed = 0;
  const paidQuotaUsed = 0;
  const paidCostUsed = 0;

  const freeQuotaPercent = freeQuotaLimit > 0 ? Math.round((freeQuotaUsed / freeQuotaLimit) * 100) : 0;
  const paidQuotaPercent = paidQuotaLimit > 0 ? Math.round((paidQuotaUsed / paidQuotaLimit) * 100) : 0;

  return {
    freeQuotaLimit, freeQuotaUsed, freeCostLimit, freeCostUsed,
    paidQuotaLimit, paidQuotaUsed, paidCostLimit, paidCostUsed,
    freeQuotaPercent, paidQuotaPercent
  };
}

/**
 * 获取全局风险预警数据
 * 性能优化：使用 SQL 聚合查询 + 指定字段查询，不 fetchAll 全表，避免读取图片 BLOB
 */
export async function getRiskAlerts(): Promise<Array<{ type: string; level: string; title: string; detail: string; count: number }>> {
  const alerts: Array<{ type: string; level: string; title: string; detail: string; count: number }> = [];
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const sevenDaysStr = sevenDaysAgo.toISOString().slice(0, 10);

  // ========== 性能优化：聚合查询 + 指定字段，替代 fetchAll 全表 ==========
  const [
    overdueFollowupCnt,
    dTierInfCnt,
    pendingApprovalCnt,
    workgroupsLite,
    returnsLite,
    samplesReturnLite,
  ] = await Promise.all([
    // 2. 样品跟进逾期（签收超7天未产出）COUNT
    countWhere(COL.SAMPLE_PROGRESS,
      `(deleted IS NULL OR deleted != 1) AND followStatus NOT IN ('已交付','达人不合作') AND signTime IS NOT NULL AND substr(signTime, 1, 10) < '${sevenDaysStr}'`
    ),
    // 3. D级达人 COUNT
    dbCount(COL.INFLUENCERS, { level: 'D' }),
    // 5. 待审批 COUNT
    dbCount(COL.SAMPLES, { approval_status: 'pending' }),
    // 1. 工作组预算预警（只查必要字段）
    queryFields<any>(COL.WORKGROUPS, ['_id', 'name', 'freeQuotaLimit'], undefined, { limit: 100 }),
    // 4. 已退回记录（只查 sample_id + return_status）
    queryFields<any>(COL.SAMPLE_RETURNS, ['sample_id', 'return_status'], undefined, { limit: 5000 }),
    // 4. 待回收样品（need_return=1+截止日）
    queryFields<any>(COL.SAMPLES, ['_id', 'need_return', 'return_deadline'],
      undefined, { limit: 5000 }),
  ]);

  // 1. 工作组预算预警
  for (const wg of workgroupsLite) {
    const usage = await getWorkgroupBudgetUsage(wg._id);
    if (usage.freeQuotaLimit > 0) {
      if (usage.freeQuotaPercent >= 100) {
        alerts.push({ type: 'budget', level: 'danger', title: `${wg.name} 预算已超限`, detail: `免费种样 ${usage.freeQuotaUsed}/${usage.freeQuotaLimit} 件，已超预算`, count: 1 });
      } else if (usage.freeQuotaPercent >= 80) {
        alerts.push({ type: 'budget', level: 'warning', title: `${wg.name} 预算即将用尽`, detail: `免费种样 ${usage.freeQuotaUsed}/${usage.freeQuotaLimit} 件，已达 ${usage.freeQuotaPercent}%`, count: 1 });
      }
    }
  }

  // 2. 样品跟进逾期
  if (overdueFollowupCnt > 0) {
    alerts.push({ type: 'followup', level: 'warning', title: '样品跟进逾期', detail: `${overdueFollowupCnt} 条样品签收超7天未产出`, count: overdueFollowupCnt });
  }

  // 3. D级达人风险
  if (dTierInfCnt > 0) {
    alerts.push({ type: 'influencer', level: 'danger', title: 'D级达人风险', detail: `${dTierInfCnt} 个达人已被标记为D级（黑名单）`, count: dTierInfCnt });
  }

  // 4. 样品回收到期预警
  const returnedIds = new Set(
    returnsLite
      .filter((r: any) => r.return_status === 'returned')
      .map((r: any) => r.sample_id)
  );
  const returnOverdue = samplesReturnLite.filter((s: any) =>
    s.need_return === 1 &&
    s.return_deadline &&
    (s.return_deadline as string) < today &&
    !returnedIds.has(s._id)
  );
  if (returnOverdue.length > 0) {
    alerts.push({ type: 'return', level: 'danger', title: '样品回收到期', detail: `${returnOverdue.length} 条样品已过回收截止日期未退回`, count: returnOverdue.length });
  }

  // 5. 待审核积压预警
  if (pendingApprovalCnt > 10) {
    alerts.push({ type: 'approval', level: 'warning', title: '待审核发样积压', detail: `${pendingApprovalCnt} 条发样单待审核`, count: pendingApprovalCnt });
  }

  return alerts;
}

/**
 * 更新达人绩效汇总
 */
export async function updateInfluencerPerformance(influencerId: string): Promise<void> {
  const [influencers, allSamples, allProgress] = await Promise.all([
    fetchAll<any>(COL.INFLUENCERS, {}, true),
    fetchAll<any>(COL.SAMPLES, {}, true),
    fetchAll<any>(COL.SAMPLE_PROGRESS, {}, true)
  ]);

  const inf = influencers.find(i => i._id === influencerId);
  if (!inf) return;

  const infSamples = allSamples.filter(s => s.accountName === inf.accountName);
  const infProgress = allProgress.filter(p => p.accountName === inf.accountName && p.deleted !== 1);
  const noOutput = infProgress.filter(p => p.followStatus && !p.followStatus.includes('产出') && !p.followStatus.includes('已交付')).length;

  const perfData = {
    influencer_id: influencerId,
    influencer_name: inf.accountName,
    platform: inf.platform,
    tier: inf.level || 'C',
    total_samples: infSamples.length,
    total_output: infProgress.filter(p => p.followStatus && p.followStatus.includes('产出')).length,
    sign_rate: infProgress.length > 0 ? `${Math.round((infProgress.filter(p => p.signTime).length / infProgress.length) * 100)}%` : '0%',
    follow_up_count: infProgress.length,
    no_output_count: noOutput,
    last_sample_date: infSamples.length > 0 ? infSamples[infSamples.length - 1].recordDate : null,
    updated_at: new Date().toISOString()
  };

  const existing = await fetchAll<any>(COL.INFLUENCER_PERFORMANCE, {}, true);
  const old = existing.find(p => p.influencer_id === influencerId);
  if (old) {
    await updateDoc(COL.INFLUENCER_PERFORMANCE, old._id, perfData);
  } else {
    await addDoc(COL.INFLUENCER_PERFORMANCE, { ...perfData, _id: generateId() });
  }
}

/**
 * 自动达人评级
 */
export async function autoUpdateInfluencerTier(influencerId: string): Promise<string | null> {
  const [influencers, allSamples, allProgress] = await Promise.all([
    fetchAll<any>(COL.INFLUENCERS, {}, true),
    fetchAll<any>(COL.SAMPLES, {}, true),
    fetchAll<any>(COL.SAMPLE_PROGRESS, {}, true)
  ]);

  const inf = influencers.find(i => i._id === influencerId);
  if (!inf) return null;

  const infProgress = allProgress.filter(p => p.accountName === inf.accountName && p.deleted !== 1);
  const totalSamples = allSamples.filter(s => s.accountName === inf.accountName).length;
  const outputCount = infProgress.filter(p => p.followStatus && p.followStatus.includes('产出')).length;
  const noOutputCount = infProgress.filter(p => p.followStatus && !p.followStatus.includes('产出') && !p.followStatus.includes('已交付')).length;

  let newTier = 'C';
  if (totalSamples === 0) {
    newTier = inf.level || 'C';
  } else {
    const outputRate = totalSamples > 0 ? outputCount / totalSamples : 0;
    if (noOutputCount >= 3) newTier = 'D';
    else if (noOutputCount >= 2) newTier = 'C';
    else if (outputRate >= 0.8) newTier = 'S';
    else if (outputRate >= 0.6) newTier = 'A';
    else if (outputRate >= 0.3) newTier = 'B';
    else newTier = 'C';
  }

  if (newTier !== inf.level) {
    await updateDoc(COL.INFLUENCERS, influencerId, { level: newTier, tier_updated_at: new Date().toISOString() });
    return newTier;
  }
  return null;
}

/**
 * 获取需要回收的样品列表
 */
export async function getReturnableSamples(): Promise<any[]> {
  const [allSamples, allReturns] = await Promise.all([
    fetchAll<any>(COL.SAMPLES, {}, true),
    fetchAll<any>(COL.SAMPLE_RETURNS, {}, true)
  ]);

  return allSamples
    .filter(s => s.need_return === 1)
    .map(s => {
      const ret = allReturns.find(r => r.sample_id === s._id);
      return {
        ...s,
        return_status: ret?.return_status || 'pending',
        return_express_no: ret?.return_express_no || '',
        return_date: ret?.return_date || '',
        receive_date: ret?.receive_date || '',
        condition_note: ret?.condition_note || '',
        return_record_id: ret?._id
      };
    });
}

// ===== 电子面单工具（第三期：打单功能扩展） =====

// 快递公司编码映射（与阿里云 API 文档一致）
export const SHIPPER_MAP: Record<string, { code: string; name: string; types: Array<{ code: string; name: string }> }> = {
  ZTO: { code: 'ZTO', name: '中通快递', types: [{ code: '23', name: '标准快递' }, { code: '21', name: '中通好快' }, { code: '22', name: '中通标快' }] },
  STO: { code: 'STO', name: '申通快递', types: [{ code: '1', name: '标准快递' }, { code: '4', name: '生鲜' }] },
  YTO: { code: 'YTO', name: '圆通速递', types: [{ code: '1', name: '标准快递' }, { code: '2', name: '圆准达' }] },
  YD: { code: 'YD', name: '韵达速递', types: [{ code: '1', name: '标准快运' }, { code: '2', name: '韵达智橙网' }] },
  YZPY: { code: 'YZPY', name: '邮政快递包裹', types: [{ code: '1', name: '特快专递' }, { code: '9', name: '快递包裹' }, { code: '10', name: '电商标快' }] },
  EMS: { code: 'EMS', name: 'EMS', types: [{ code: '1', name: '特快专递' }, { code: '9', name: '快递包裹' }] },
  SF: { code: 'SF', name: '顺丰速运', types: [{ code: '1', name: '顺丰特快' }, { code: '2', name: '顺丰标快' }, { code: '6', name: '顺丰即日' }] },
  JD: { code: 'JD', name: '京东快递', types: [{ code: '1', name: '京东标快' }, { code: '2', name: '京东特快' }] },
  JDKY: { code: 'JDKY', name: '京东快运', types: [{ code: '1', name: '京东标快' }] },
  UC: { code: 'UC', name: '优速快递', types: [{ code: '1', name: '标准快递' }] },
  DBL: { code: 'DBL', name: '德邦快递', types: [{ code: '1', name: '标准快递' }, { code: '5', name: '大件快递360' }] },
  JTSD: { code: 'JTSD', name: '极兔速递', types: [{ code: '1', name: '标准快递' }, { code: '2', name: '兔优达' }] },
  CNSD: { code: 'CNSD', name: '菜鸟速递', types: [{ code: '1', name: '当日达' }, { code: '2', name: '次日达' }] },
  CNCY: { code: 'CNCY', name: '菜鸟橙运', types: [{ code: '101', name: '当日达' }, { code: '103', name: '次日达' }] },
  DNWL: { code: 'DNWL', name: '丹鸟物流', types: [{ code: '1', name: '标准快递' }] },
  ANE: { code: 'ANE', name: '安能快递', types: [{ code: '23', name: '定时达' }, { code: '270', name: '普惠达' }] },
};

// 根据 shipperCode 获取中文名
export function getShipperName(code: string): string {
  return SHIPPER_MAP[code]?.name || code;
}

// 地址智能拆分：把「浙江省杭州市余杭区XX路1号」拆成省/市/区/详细地址
export function splitAddress(fullAddr: string): { province: string; city: string; area: string; address: string } {
  if (!fullAddr) return { province: '', city: '', area: '', address: '' };
  let addr = fullAddr.trim();
  let province = '', city = '', area = '';

  // 省份匹配（含直辖市、自治区）
  const provinceMatch = addr.match(/^(北京|天津|上海|重庆|河北|山西|辽宁|吉林|黑龙江|江苏|浙江|安徽|福建|江西|山东|河南|湖北|湖南|广东|海南|四川|贵州|云南|陕西|甘肃|青海|台湾|内蒙古|广西|西藏|宁夏|新疆|香港|澳门)(?:省|市|自治区|特别行政区)?/);
  if (provinceMatch) {
    province = provinceMatch[1];
    // 补全省份后缀
    if (['北京', '天津', '上海', '重庆'].includes(province)) province += '市';
    else if (['内蒙古', '广西', '西藏', '宁夏', '新疆'].includes(province)) province += '自治区';
    else if (['香港', '澳门'].includes(province)) province += '特别行政区';
    else province += '省';
    addr = addr.slice(provinceMatch[0].length);
  }

  // 市匹配
  const cityMatch = addr.match(/^([\u4e00-\u9fa5]+?)(市|地区|州|盟)/);
  if (cityMatch) {
    city = cityMatch[1] + cityMatch[2];
    addr = addr.slice(cityMatch[0].length);
  }

  // 区/县匹配
  const areaMatch = addr.match(/^([\u4e00-\u9fa5]+?)(区|县|市|旗|自治县|自治旗)/);
  if (areaMatch) {
    area = areaMatch[1] + areaMatch[2];
    addr = addr.slice(areaMatch[0].length);
  }

  return { province, city, area, address: addr.trim() };
}

// 面单配置缓存（前端层，从商家端 server.js 获取）
let _waybillConfigCache: any = null;
let _waybillConfigCacheTime = 0;

// 获取电子面单配置（优先商家端本地个性化配置，无本地配置则回退管理端共享默认值；非敏感，不含 AppCode）
export async function getWaybillConfig(): Promise<any> {
  const now = Date.now();
  if (_waybillConfigCache && now - _waybillConfigCacheTime < 60 * 1000) {
    return _waybillConfigCache;
  }
  const token = await getRemoteToken();
  let localCfg: any = null;
  try {
    const localResp = await fetch(`${LOCAL_API_URL}/waybill/local-config`, {
      headers: { 'Authorization': `Bearer ${token}`, ...getAppBuildHeaders() }
    });
    if (localResp.ok) {
      localCfg = await localResp.json();
    }
  } catch (e) {
    console.warn('[getWaybillConfig] 读取本地配置失败，使用远程配置:', (e as Error).message);
  }
  const resp = await fetch(`${REMOTE_API_URL}/waybill/config`, {
    headers: { 'Authorization': `Bearer ${token}`, ...getAppBuildHeaders() }
  });
  if (!resp.ok) throw new Error(`获取面单配置失败: ${resp.status}`);
  const remoteCfg = await resp.json();
  let merged = remoteCfg;
  if (localCfg && localCfg.hasLocalConfig) {
    merged = { ...remoteCfg };
    const s = localCfg.sender || {};
    merged.sender = {
      company:  s.company  || remoteCfg.sender?.company  || '',
      name:     s.name     || remoteCfg.sender?.name     || '',
      mobile:   s.mobile   || remoteCfg.sender?.mobile   || '',
      province: s.province || remoteCfg.sender?.province || '',
      city:     s.city     || remoteCfg.sender?.city     || '',
      area:     s.area     || remoteCfg.sender?.area     || '',
      address:  s.address  || remoteCfg.sender?.address  || '',
    };
    if (localCfg.defaultShipper) merged.defaultShipper = localCfg.defaultShipper;
    if (localCfg.defaultPayType) merged.defaultPayType = localCfg.defaultPayType;
    if (localCfg.templateSize)   merged.templateSize = localCfg.templateSize;
    if (localCfg.defaultWeight)  merged.defaultWeight = localCfg.defaultWeight;
    if (Array.isArray(localCfg.shipperDefaults) && localCfg.shipperDefaults.length > 0) {
      merged.shipperDefaults = localCfg.shipperDefaults;
    }
    merged.hasLocalConfig = true;
  }
  _waybillConfigCache = merged;
  _waybillConfigCacheTime = now;
  return merged;
}

// 保存商家端本地电子面单个性化配置
export async function saveLocalWaybillConfig(cfg: {
  sender?: { company?: string; name?: string; mobile?: string; province?: string; city?: string; area?: string; address?: string };
  defaultShipper?: string;
  defaultPayType?: string;
  templateSize?: string;
  defaultWeight?: string;
  shipperDefaults?: Array<{ shipperCode: string; monthCode: string; expType: string }>;
  printMode?: 'local' | 'cloud';
  defaultPrinter?: string;
  copies?: number;
  cloudPrinterId?: string;
}): Promise<{ success: boolean; message?: string; error?: string }> {
  const token = await getRemoteToken();
  const resp = await fetch(`${LOCAL_API_URL}/waybill/local-config`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(cfg)
  });
  const data = await resp.json();
  _waybillConfigCache = null;
  _waybillConfigCacheTime = 0;
  if (!resp.ok) return { success: false, error: data.error || `HTTP ${resp.status}` };
  return { success: true, message: data.message };
}

// 调用电子面单下单接口（单条）
export async function createWaybillOrder(params: {
  shipperCode: string;
  payType: string;
  expType: string;
  monthCode?: string;
  templateSize?: string;
  weight?: number;
  sender: any;
  receiver: any;
  commodity?: any[];
  remark?: string;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  const token = await getRemoteToken();
  const resp = await fetch(`${REMOTE_API_URL}/waybill/order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });
  const data = await resp.json();
  if (!resp.ok) {
    return { success: false, error: data.error || `HTTP ${resp.status}` };
  }
  // 下单成功后扣减管理端共享电子面单库存（失败不阻断主流程）
  consumeStock('waybill', 1, params.receiver?.name || params.shipperCode).catch(e => console.warn('[createWaybillOrder] 共享库存扣减失败:', e));
  return { success: true, data: data.data };
}

// 批量生成电子面单
export async function createWaybillOrderBatch(orders: any[]): Promise<{
  success: boolean;
  total: number;
  successCount: number;
  failedCount: number;
  results: any[];
  error?: string;
}> {
  const token = await getRemoteToken();
  const resp = await fetch(`${REMOTE_API_URL}/waybill/order/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ orders })
  });
  const data = await resp.json();
  if (!resp.ok) {
    return { success: false, total: orders.length, successCount: 0, failedCount: orders.length, results: [], error: data.error || `HTTP ${resp.status}` };
  }
  // 批量下单成功后按成功数扣减共享电子面单库存（失败不阻断主流程）
  const okCount = data.successCount || 0;
  if (okCount > 0) {
    consumeStock('waybill', okCount, '批量下单').catch(e => console.warn('[createWaybillOrderBatch] 共享库存扣减失败:', e));
  }
  return {
    success: true,
    total: data.total,
    successCount: data.successCount,
    failedCount: data.failedCount,
    results: data.results
  };
}

// 从阿里云返回数据中提取运单号（兼容多种返回字段名）
export function extractWaybillNo(apiData: any): string {
  if (!apiData) return '';
  return apiData.LogisticsCode || apiData.logisticsCode || apiData.WaybillCode || apiData.waybillCode || apiData.OrderCode || apiData.orderCode || '';
}

// 从阿里云返回数据中提取面单模板（HTML/图片）
export function extractPrintTemplate(apiData: any): string {
  if (!apiData) return '';
  return apiData.PrintTemplate || apiData.printTemplate || apiData.Template || apiData.template || '';
}



// ========== 快递100 云打印 API ==========
export async function getCloudPrintPrinters(): Promise<{ success: boolean; printers: any[]; error?: string }> {
  try {
    const token = await getRemoteToken();
    const resp = await fetch(`${LOCAL_API_URL}/waybill/cloud-print/printers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return await resp.json();
  } catch (e: any) {
    return { success: false, printers: [], error: e.message };
  }
}

export async function bindCloudPrinter(printerId: string, printerName?: string): Promise<{ success: boolean; error?: string }> {
  try {
    const token = await getRemoteToken();
    const resp = await fetch(`${LOCAL_API_URL}/waybill/cloud-print/bind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ printerId, printerName }),
    });
    return await resp.json();
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}

export async function unbindCloudPrinter(printerId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const token = await getRemoteToken();
    const resp = await fetch(`${LOCAL_API_URL}/waybill/cloud-print/unbind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ printerId }),
    });
    return await resp.json();
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}

export async function sendCloudPrint(params: { printerId: string; orderId?: string; kuaidinum?: string; labelUrl?: string; copy?: number }): Promise<{ success: boolean; error?: string; data?: any }> {
  try {
    const token = await getRemoteToken();
    const resp = await fetch(`${LOCAL_API_URL}/waybill/cloud-print/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8', Authorization: `Bearer ${token}` },
      body: JSON.stringify(params),
    });
    return await resp.json();
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}
